/**
 * SINGLE SOURCE OF TRUTH FOR PRICING CONFIGURATION
 *
 * This file is shared between all packages that need model pricing
 * and plan configuration (app, proxy-api, proxy-api-job, etc.).
 *
 * Public unit: USD.
 * Internal storage unit: milli-dollars (1 milli-dollar = $0.001).
 *
 * Legacy database columns and some compatibility APIs still use "credit" names
 * during migration. Those numeric values are milli-dollars.
 */

// ========================================
// TIER MULTIPLIERS
// ========================================

/** Valid provider tier options - single source of truth */
export const VALID_PROVIDER_TIERS = ["economy", "standard", "premium"] as const;

/** Provider tier type derived from VALID_PROVIDER_TIERS */
export type ProviderTier = (typeof VALID_PROVIDER_TIERS)[number];

// ========================================
// TIER MULTIPLIERS (Wallet-Based Discounts)
// ========================================

/**
 * The multiplier applied to a request's raw provider cost now depends on
 * on the tier and model. Bonus and prepaid wallets use the same resolved
 * multiplier, so splitting a request across them never changes its price.
 *
 * Wallet eligibility (separate from rating):
 * - Economy / Standard tier: bonus wallet or prepaid wallet
 * - Premium tier: prepaid wallet only (see PremiumTierAuthorization)
 */
/**
 * Default prepaid-wallet multipliers applied to the raw provider cost.
 * - Economy tier: 0.2x (80% discount)
 * - Standard tier: 0.5x (50% discount)
 * - Premium tier: 1.0x (no discount, full price)
 */
export const PREPAID_TIER_MULTIPLIERS: Record<ProviderTier, number> = {
  economy: 0.2,  // 80% discount
  standard: 0.5, // 50% discount
  premium: 1.0,  // No discount (full price)
};

/** Backward compatibility alias - defaults to prepaid rates. */
export const DEFAULT_TIER_MULTIPLIERS = PREPAID_TIER_MULTIPLIERS;

/**
 * Multiplier used by CostEstimator's pre-flight sizing. Since both eligible
 * wallets use the same resolved rate, this is the prepaid multiplier for the
 * requested tier and model. The estimate must be scaled by it to preserve the
 * "an admitted request always settles
 * within MAX_OVERDRAFT_MILLIDOLLARS" guarantee. Premium has no bonus rate, so
 * it's always exactly its prepaid rate (1.0x).
 */
export function getWorstCaseTierMultiplier(tier: ProviderTier, modelId: string): number {
  return getPrepaidTierMultiplier(tier, modelId);
}

/**
 * Per-model overrides for the Standard tier's PREPAID multiplier. Sparse —
 * most models use PREPAID_TIER_MULTIPLIERS.standard (0.5x) and don't need an
 * entry here. Keyed by canonical model id (see
 * resolveCanonicalModelId/normalizeModelId), so overrides apply regardless of
 * which alias/prefix a request used.
 *
 * Premium tier is never overridden. Bonus uses this same resolved rate when
 * the tier is bonus-eligible.
 *
 * Resolution order (see getStandardTierMultiplier): this map, then
 * STANDARD_PREPAID_MULTIPLIER_FAMILY_OVERRIDE, then
 * PREPAID_TIER_MULTIPLIERS.standard.
 */
export const STANDARD_PREPAID_MULTIPLIER_OVERRIDE: Partial<Record<ModelId, number>> = {
  "gpt-5.4": 0.2,
  'gpt-5.6-terra': 0.2,
};

/**
 * Per-family fallback for the Standard tier's PREPAID multiplier — used only
 * when the model has no entry of its own in
 * STANDARD_PREPAID_MULTIPLIER_OVERRIDE. Sparse; most families use
 * PREPAID_TIER_MULTIPLIERS.standard.
 *
 * Resolution order: STANDARD_PREPAID_MULTIPLIER_OVERRIDE (per model) >
 * this map (per family) > PREPAID_TIER_MULTIPLIERS.standard (default).
 */
export const STANDARD_PREPAID_MULTIPLIER_FAMILY_OVERRIDE: Partial<
  Record<ModelFamily, number>
> = {
  'anthropic': 0.35,
  'openai': 0.2
};

/**
 * Per-model overrides for the Economy tier's PREPAID multiplier. Sparse and
 * currently empty — no model needs a rate other than
 * PREPAID_TIER_MULTIPLIERS.economy (0.2x) yet. Mirrors
 * STANDARD_PREPAID_MULTIPLIER_OVERRIDE's shape/resolution order.
 */
export const ECONOMY_PREPAID_MULTIPLIER_OVERRIDE: Partial<Record<ModelId, number>> = {};

/**
 * Per-family fallback for the Economy tier's PREPAID multiplier. Sparse and
 * currently empty. Mirrors STANDARD_PREPAID_MULTIPLIER_FAMILY_OVERRIDE's
 * shape/resolution order.
 */
export const ECONOMY_PREPAID_MULTIPLIER_FAMILY_OVERRIDE: Partial<
  Record<ModelFamily, number>
> = {};

// ========================================
// SUBSCRIPTION PLAN TYPES
// ========================================

/** Time-windowed subscription plan tiers - single source of truth */
export type PlanTier = "starter" | "builder" | "pro" | "power" | "max";

/** Subscription status - single source of truth */
export type SubscriptionStatus =
  | "pending"
  | "active"
  | "canceled"
  | "past_due"
  | "expired";

/** Rate limits (requests per minute) for each plan tier - single source of truth */
export const PLAN_RATE_LIMITS: Record<PlanTier, number> = {
  starter: 120,
  builder: 120,
  pro: 120,
  power: 120,
  max: 180,
} as const;

/** Rate limit for free tier users (no active subscription) */
export const FREE_TIER_RPM = 120;

// ========================================
// MONEY CONSTANTS
// ========================================

export const MILLIDOLLARS_PER_DOLLAR = 1_000;

// ========================================
// HELPERS
// ========================================

export function dollarsToMilliDollars(dollars: number): number {
  // Use Math.ceil to prevent micro-transaction undercharging.
  return Math.ceil(dollars * MILLIDOLLARS_PER_DOLLAR);
}

export function milliDollarsToDollars(milliDollars: number): number {
  return milliDollars / MILLIDOLLARS_PER_DOLLAR;
}

export function formatDollars(
  milliDollars: number,
  options: {
    compact?: boolean;
    minimumFractionDigits?: number;
    maximumFractionDigits?: number;
  } = {},
): string {
  const dollars = milliDollarsToDollars(milliDollars);
  const abs = Math.abs(dollars);
  const sign = dollars < 0 ? "-" : "";
  const minimumFractionDigits = options.minimumFractionDigits ?? 2;
  const maximumFractionDigits =
    options.maximumFractionDigits ?? (abs < 0.01 && abs > 0 ? 6 : 2);

  if (options.compact && abs >= 1_000_000) {
    return `${sign}$${(abs / 1_000_000).toFixed(1).replace(/\.0$/, "")}M`;
  }
  if (options.compact && abs >= 10_000) {
    return `${sign}$${(abs / 1_000).toFixed(1).replace(/\.0$/, "")}K`;
  }

  return `${sign}$${abs.toLocaleString("en-US", {
    minimumFractionDigits,
    maximumFractionDigits,
  })}`;
}

/**
 * Top-up-only entitlements (no active subscription)
 */
export const TOP_UP_ONLY_ENTITLEMENTS = {
  rpm: 40,
  apiKeyLimit: 10,
  stackLimit: 10,
  modelsPerStack: 3,
  freeRoutingRequestsPerDay: 0, // No free routing requests without subscription
  analyticsRetentionDays: 7,
  supportLevel: "community",
  earlyAccess: false,
} as const;

// ========================================
// PLAN CONFIGURATIONS (single source of truth)
// ========================================

export interface PlanConfig {
  tier: PlanTier;
  name: string;
  price: number; // Monthly price in dollars
  rateLimit: number; // Requests per minute (plan entitlement)
  maxApiKeys: number; // -1 for unlimited
  stackLimit: number; // -1 for unlimited
  modelsPerStack: number; // -1 for unlimited
  analyticsRetentionDays: number;
  supportLevel: "community" | "email" | "priority";
  freeRoutingRequestsPerDay: number; // Free routing requests per day
  earlyAccess: boolean;
  // Time-windowed subscription caps (in millidollars: 1 millidollar = $0.001)
  windowCapMillidollars: number; // 5-hour window spending cap
  weeklyCapMillidollars: number; // 7-day weekly spending cap
  premiumBudgetMillidollars: number; // Monthly premium tier budget
}

export const PLAN_CONFIGS: Record<PlanTier, PlanConfig> = {
  starter: {
    tier: "starter",
    name: "Starter",
    price: 10,
    rateLimit: PLAN_RATE_LIMITS.starter,
    maxApiKeys: 5,
    stackLimit: 3,
    modelsPerStack: 2,
    analyticsRetentionDays: 7,
    supportLevel: "community",
    freeRoutingRequestsPerDay: 100,
    earlyAccess: false,
    windowCapMillidollars: 20000, // $20 per 5-hour window
    weeklyCapMillidollars: 120000, // $120 per week
    premiumBudgetMillidollars: 1000, // $1 per month
  },
  builder: {
    tier: "builder",
    name: "Builder",
    price: 25,
    rateLimit: PLAN_RATE_LIMITS.builder,
    maxApiKeys: 10,
    stackLimit: 10,
    modelsPerStack: 5,
    analyticsRetentionDays: 90,
    supportLevel: "email",
    freeRoutingRequestsPerDay: 5000,
    earlyAccess: true,
    windowCapMillidollars: 30000, // $30 per 5-hour window
    weeklyCapMillidollars: 300000, // $300 per week
    premiumBudgetMillidollars: 1000, // $1 per month
  },
  pro: {
    tier: "pro",
    name: "Pro",
    price: 50,
    rateLimit: PLAN_RATE_LIMITS.pro,
    maxApiKeys: 20,
    stackLimit: 10,
    modelsPerStack: 5,
    analyticsRetentionDays: 30,
    supportLevel: "email",
    freeRoutingRequestsPerDay: 500,
    earlyAccess: false,
    windowCapMillidollars: 50000, // $50 per 5-hour window
    weeklyCapMillidollars: 750000, // $750 per week
    premiumBudgetMillidollars: 2000, // $2 per month
  },
  power: {
    tier: "power",
    name: "Power",
    price: 100,
    rateLimit: PLAN_RATE_LIMITS.power,
    maxApiKeys: -1,
    stackLimit: -1,
    modelsPerStack: -1,
    analyticsRetentionDays: 365,
    supportLevel: "priority",
    freeRoutingRequestsPerDay: 20000,
    earlyAccess: true,
    windowCapMillidollars: 100000, // $100 per 5-hour window
    weeklyCapMillidollars: 1500000, // $1,500 per week
    premiumBudgetMillidollars: 5000, // $5 per month
  },
  max: {
    tier: "max",
    name: "Max",
    price: 200,
    rateLimit: PLAN_RATE_LIMITS.max,
    maxApiKeys: -1,
    stackLimit: -1,
    modelsPerStack: 10,
    analyticsRetentionDays: 90,
    supportLevel: "priority",
    freeRoutingRequestsPerDay: 1_000,
    earlyAccess: true,
    windowCapMillidollars: 200000, // $200 per 5-hour window
    weeklyCapMillidollars: 3600000, // $3,600 per week
    premiumBudgetMillidollars: 10000, // $10 per month
  },
};

export interface CreditDepositOption {
  amount: number; // In dollars
  amountMilliDollars: number;
  label: string;
}

export const CREDIT_DEPOSIT_OPTIONS: Array<CreditDepositOption> = [
  3, 10, 30, 50, 100, 300,
].map((amount) => ({
  amount,
  amountMilliDollars: dollarsToMilliDollars(amount),
  label: `$${amount.toLocaleString()}`,
}));

/**
 * Time-Windowed Subscription Plan Configuration
 * New subscription model with rolling time windows and spending caps
 */
export interface TimeWindowedPlanConfig {
  tier: PlanTier;
  name: string;
  monthlyFee: number; // Monthly subscription fee in dollars
  windowDurationHours: number; // Duration of each spending window (default: 5 hours)
  windowCapMillidollars: number; // Spending cap per window in millidollars
  weeklyCapMillidollars: number; // Weekly spending cap in millidollars
  premiumBudgetMillidollars: number; // Monthly premium model budget in millidollars
  rateLimit: number; // Requests per minute
  maxApiKeys: number; // -1 for unlimited
  stackLimit: number; // -1 for unlimited
  modelsPerStack: number; // -1 for unlimited
  analyticsRetentionDays: number;
  supportLevel: "community" | "email" | "priority";
  freeRoutingRequestsPerDay: number;
  earlyAccess: boolean;
}

/**
 * Time-Windowed Plan Configurations
 * Derived from PLAN_CONFIGS to maintain single source of truth
 */
export const TIME_WINDOWED_PLAN_CONFIGS: Record<
  PlanTier,
  TimeWindowedPlanConfig
> = Object.fromEntries(
  Object.entries(PLAN_CONFIGS).map(([tier, config]) => [
    tier,
    {
      tier: config.tier,
      name: config.name,
      monthlyFee: config.price,
      windowDurationHours: 5,
      windowCapMillidollars: config.windowCapMillidollars,
      weeklyCapMillidollars: config.weeklyCapMillidollars,
      premiumBudgetMillidollars: config.premiumBudgetMillidollars,
      rateLimit: config.rateLimit,
      maxApiKeys: config.maxApiKeys,
      stackLimit: config.stackLimit,
      modelsPerStack: config.modelsPerStack,
      analyticsRetentionDays: config.analyticsRetentionDays,
      supportLevel: config.supportLevel,
      freeRoutingRequestsPerDay: config.freeRoutingRequestsPerDay,
      earlyAccess: config.earlyAccess,
    },
  ]),
) as Record<PlanTier, TimeWindowedPlanConfig>;

/**
 * List of all time-windowed plan tiers
 * Derived from TIME_WINDOWED_PLAN_CONFIGS keys to maintain single source of truth
 */
export const TIME_WINDOWED_PLAN_TIERS = Object.keys(
  TIME_WINDOWED_PLAN_CONFIGS,
) as Array<PlanTier>;

/**
 * Check if a plan tier uses time-windowed billing
 */
export function isTimeWindowedPlan(tier: PlanTier | null | undefined): boolean {
  return tier ? tier in TIME_WINDOWED_PLAN_CONFIGS : false;
}

/**
 * Prepaid balance expiration period (in months)
 */
export const PREPAID_BALANCE_EXPIRATION_MONTHS = 12;

/**
 * Bonus balance (referral rewards, gift codes) expiration period (in days)
 */
export const BONUS_BALANCE_EXPIRATION_DAYS = 30;

/**
 * How far a wallet may be overdrawn at settlement, in milli-dollars ($0.50).
 *
 * Settlement happens asynchronously, after the response has already been
 * streamed, so the charge can legitimately exceed what the pre-flight check
 * saw: BillingCheckMiddleware.calculateRiskBuffer deliberately admits requests
 * that are short by up to 400 milli-dollars, and a streamed response's token
 * count is not knowable up front.
 *
 * A wallet must therefore be able to go negative — otherwise settlement fails,
 * the queue message dead-letters, and usage we already served is never billed.
 * The floor bounds that exposure so a genuine accounting bug (e.g. an expiry
 * job double-deducting) still trips the CHECK constraint loudly instead of
 * drifting silently.
 *
 * Once a wallet is at or below zero, BillingCheckMiddleware blocks further
 * requests (both wallet checks require `> 0`), so the debt is self-limiting and
 * nets off against the next top-up or grant.
 *
 * Keep in sync with the `bonus_balance_floor` / `prepaid_balance_floor` CHECK
 * constraints in packages/shared-schema/src/auth.ts.
 */
export const MAX_OVERDRAFT_MILLIDOLLARS = 500;

/**
 * Fraction of the maximum possible output cost assumed when estimating a
 * request's cost up front (CostEstimator).
 *
 * Input tokens are counted exactly, but output length is unknown until the
 * response finishes. Pricing the output at its ceiling makes the pre-flight
 * check demand far more than a typical request actually costs and blocks users
 * who could comfortably pay; most completions use a fraction of `max_tokens`.
 *
 * CostEstimator clamps this against MAX_OVERDRAFT_MILLIDOLLARS so the shortfall
 * on a request that DOES run to its ceiling can never exceed the overdraft
 * floor. Expensive requests therefore tighten toward worst-case automatically,
 * and this fraction only relaxes the estimate where it is safe to do so.
 */
export const OUTPUT_ESTIMATE_FRACTION = 0.5;

// ========================================
// AGENT ROLES, MODEL PRICING, AND CAPABILITIES
// ========================================

export interface AgentRole {
  /** Unique identifier for the role (used in assignments) */
  id: string;
  /** Display name for the role */
  name: string;
  /** Description of what this role does */
  description: string;
  /** Whether this role is required in every modelstack */
  required: boolean;
}

/**
 * All available agent roles in the system
 */
export const AGENT_ROLES: AgentRole[] = [
  {
    id: "junior",
    name: "Junior Agent",
    description:
      "Handles all client communication, uses cheapest capable model",
    required: true,
  },
  {
    id: "senior",
    name: "Senior Agent",
    description: "Expert advisor consulted for complex tasks",
    required: true,
  },
  {
    id: "strategist",
    name: "Strategist",
    description: "Deep reasoning for architectural decisions",
    required: true,
  },
];

/**
 * Get an agent role by its ID
 * @param id - The role ID to look up
 * @returns The agent role, or undefined if not found
 */
export function getAgentRoleById(id: string): AgentRole | undefined {
  return AGENT_ROLES.find((role) => role.id === id);
}

/**
 * Get all required agent roles
 * @returns Array of required agent roles
 */
export function getRequiredAgentRoles(): AgentRole[] {
  return AGENT_ROLES.filter((role) => role.required);
}

/**
 * Validate that a role ID is valid
 * @param id - The role ID to validate
 * @returns true if the role ID is valid, false otherwise
 */
export function validateAgentRoleId(id: string): boolean {
  return AGENT_ROLES.some((role) => role.id === id);
}

/**
 * Get all role IDs
 * @returns Array of all role IDs
 */
export function getAllAgentRoleIds(): string[] {
  return AGENT_ROLES.map((role) => role.id);
}

// ========================================
// MODEL PRICING AND CAPABILITIES
// ========================================

export type ModelFamily =
  | "anthropic"
  | "openai"
  | "google"
  | "kling"
  | "deepseek"
  | "bytedance"
  | "minimax"
  | "zai"
  | "qwen"
  | "moonshot"
  | "xai"
  | "stepfun"
  | "xiaomi"
  | "wan"
  | "unknown";

export type ModelCapability =
  | "chat" // General conversational
  | "reasoning" // Deep chain-of-thought / planning
  | "coding" // Code generation / refactoring
  | "vision" // Image understanding
  | "image-gen" // Image generation
  | "search" // Web / deep search
  | "fast" // Low latency
  | "cheap" // Cost-optimized
  | "tool_use" // Tool use
  | "analysis" // Analysis
  | "long_context"; // Long context

/**
 * Derive model capabilities from supportedParams
 * This is the single source of truth for what a model can do
 */
export function deriveCapabilitiesFromParams(
  supportedParams: string[] | undefined,
  family: ModelFamily,
): ModelCapability[] {
  if (!supportedParams) return ["chat"];

  const capabilities: ModelCapability[] = [];

  // All models support chat
  capabilities.push("chat");

  // Tool use - has tools or tool_choice
  if (
    supportedParams.includes("tools") ||
    supportedParams.includes("tool_choice")
  ) {
    capabilities.push("tool_use");
  }

  // Reasoning - has thinking parameter
  if (supportedParams.includes("thinking")) {
    capabilities.push("reasoning");
  }

  // Vision - Anthropic, OpenAI, Google models with messages support vision
  // (messages can contain image content blocks)
  if (supportedParams.includes("messages")) {
    if (family === "anthropic" || family === "openai" || family === "google") {
      // These providers support vision through messages
      capabilities.push("vision");
    }
  }

  // Coding - models with tool_use typically support coding
  if (capabilities.includes("tool_use")) {
    capabilities.push("coding");
  }

  // Analysis - models with reasoning typically support analysis
  if (capabilities.includes("reasoning")) {
    capabilities.push("analysis");
  }

  // Long context - models with cache_control typically have long context
  if (supportedParams.includes("cache_control")) {
    capabilities.push("long_context");
  }

  // Search - has web_search tool
  if (supportedParams.some((p) => p.includes("web_search"))) {
    capabilities.push("search");
  }

  return capabilities;
}

/** Model pricing for chat/text LLMs with token-based pricing */
export interface ModelPricing {
  family: ModelFamily;
  pricingSourceModelId?: string;
  pricingSourceUrl?: string;
  pricingSourceCheckedAt?: string;
  pricingNotes?: string;
  description: string;
  strongestFor?: string;
  releasedAt?: string;
  // Catalog fields (flattened from ModelCatalogMetadata)
  displayName: string;
  version?: string;
  latency?: number;
  dataCutoff?: string;
  icon?: string;
  featured?: boolean;
  // Supported API parameters (single source of truth for capabilities)
  supportedParams?: string[];
  // Token-based pricing
  inputPricePerMillion: number;
  outputPricePerMillion: number;
  cacheReadPricePerMillion: number;
  // 5-minute cache write rate (Anthropic: 1.25x base input). This is the default
  // cache-creation rate and what `cacheCreationPricePerMillion` has always meant.
  cacheCreationPricePerMillion: number;
  // 1-hour cache write rate (Anthropic: 2x base input). Optional; when omitted the
  // CostCalculator derives it as inputPricePerMillion * 2. Only Anthropic reports a
  // 5m/1h cache-creation split, so this stays unused for other families.
  cacheCreation1hPricePerMillion?: number;
  maxContextTokens: number;
  maxContext: string;
  maxOutput: string;
  // Routing capabilities
  routing?: ModelCapabilities;
}

export function familyToProvider(family: string): string {
  const familyMap: Record<ModelFamily, string> = {
    anthropic: "Anthropic",
    openai: "OpenAI",
    google: "Google",
    deepseek: "DeepSeek",
    minimax: "MiniMax",
    zai: "ZAI",
    qwen: "Qwen",
    moonshot: "Moonshot",
    xai: "xAI",
    stepfun: "StepFun",
    xiaomi: "Xiaomi",
    wan: "Wan",
    kling: "Kling",
    bytedance: "ByteDance",
    unknown: "Unknown",
  };
  return familyMap[family.toLowerCase() as ModelFamily] || "Unknown";
}

export const BASE_RATE = 1; // No discount applied (100% of provider cost)

const OFFICIAL_MODEL_PRICING = {
  // ========================================
  // ANTHROPIC MODELS
  // Verified against OpenRouter model pricing (checked 2026-05-22).
  // ========================================
  "claude-opus-4-8": {
    family: "anthropic",
    inputPricePerMillion: 5,
    outputPricePerMillion: 25,
    cacheReadPricePerMillion: 0.5,
    cacheCreationPricePerMillion: 6.25,
    cacheCreation1hPricePerMillion: 10,
    pricingSourceModelId: "anthropic/claude-opus-4-8",
    pricingSourceUrl: "https://www.anthropic.com/news/claude-opus-4-8",
    pricingSourceCheckedAt: "2026-06-02",
    maxContextTokens: 1_000_000,
    description:
      "Latest and most capable model for complex reasoning and agentic coding. Flagship model with improved reliability and judgment.",
    strongestFor:
      "Complex multi-step agentic tasks, long-horizon coding, professional work, error detection, honest responses",
    releasedAt: "2026-05-28",
    displayName: "Claude Opus 4.8",
    version: "Opus 4.8",
    maxContext: "1M",
    maxOutput: "128K",
    latency: 20,
    dataCutoff: "2026-01",
    icon: "psychology",
    featured: true,
    supportedParams: [
      "model",
      "messages",
      "max_tokens",
      "temperature",
      "top_p",
      "top_k",
      "stop_sequences",
      "stream",
      "system",
      "metadata",
      "tools",
      "tool_choice",
      "thinking",
      "output_config",
      "cache_control",
      "service_tier",
      "container",
      "inference_geo",
    ],
    routing: {
      modelId: "claude-opus-4-8",
      description:
        "Anthropic's newest flagship — frontier model for agents, coding, complex reasoning, and long-context work (1M context)",
      strongestFor: [
        "text_generalist",
        "coding",
        "reasoning",
        "math",
        "long_context",
        "tool_use",
        "high_safety",
      ],
      scores: {
        code: 0.97,
        math: 0.97,
        writing: 0.95,
        analysis: 0.97,
        qa_factual: 0.95,
        translation: 0.92,
        extraction: 0.96,
        classification: 0.94,
        creative: 0.95,
        reasoning: 0.98,
        long_context: 0.98,
        tool_use: 0.97,
        multimodal: 0.94,
        safety: 0.97,
      },
    },
  },
  "claude-opus-4-6": {
    family: "anthropic",
    inputPricePerMillion: 5,
    outputPricePerMillion: 25,
    cacheReadPricePerMillion: 0.5,
    cacheCreationPricePerMillion: 6.25,
    cacheCreation1hPricePerMillion: 10,
    pricingSourceModelId: "anthropic/claude-opus-4.6",
    pricingSourceUrl: "https://openrouter.ai/anthropic/claude-opus-4.6",
    pricingSourceCheckedAt: "2026-05-22",
    maxContextTokens: 1_000_000,
    description:
      "Previous flagship model for building agents and coding. Strong performance with 1M context.",
    strongestFor:
      "Complex multi-step agentic tasks, financial analysis, research, long-context reasoning",
    releasedAt: "2026-02",
    displayName: "Claude Opus 4.6",
    version: "Opus 4.6",
    maxContext: "1M",
    maxOutput: "128K",
    latency: 20,
    icon: "psychology",
    featured: true,
    supportedParams: [
      "model",
      "messages",
      "max_tokens",
      "temperature",
      "top_p",
      "top_k",
      "stop_sequences",
      "stream",
      "system",
      "metadata",
      "tools",
      "tool_choice",
      "thinking",
      "output_config",
      "cache_control",
      "service_tier",
      "container",
      "inference_geo",
    ],
    routing: {
      modelId: "claude-opus-4-6",
      description:
        "Anthropic flagship — frontier model for agents, coding, complex reasoning, and long-context work (1M context)",
      strongestFor: [
        "text_generalist",
        "coding",
        "reasoning",
        "math",
        "long_context",
        "tool_use",
        "high_safety",
      ],
      scores: {
        code: 0.95,
        math: 0.95,
        writing: 0.94,
        analysis: 0.95,
        qa_factual: 0.93,
        translation: 0.9,
        extraction: 0.94,
        classification: 0.92,
        creative: 0.93,
        reasoning: 0.96,
        long_context: 0.96,
        tool_use: 0.95,
        multimodal: 0.92,
        safety: 0.96,
      },
    },
  },
  "claude-opus-4-7": {
    family: "anthropic",
    inputPricePerMillion: 5,
    outputPricePerMillion: 25,
    cacheReadPricePerMillion: 0.5,
    cacheCreationPricePerMillion: 6.25,
    cacheCreation1hPricePerMillion: 10,
    pricingSourceModelId: "anthropic/claude-opus-4.7",
    pricingSourceUrl: "https://openrouter.ai/anthropic/claude-opus-4.7",
    pricingSourceCheckedAt: "2026-05-22",
    maxContextTokens: 1_000_000,
    description: "Legacy flagship model. Superseded by Opus 4.6.",
    strongestFor: "Complex coding and agentic tasks (legacy)",
    releasedAt: "2026-04",
    displayName: "Claude Opus 4.7",
    version: "Opus 4.7",
    maxContext: "1M",
    maxOutput: "128K",
    latency: 20,
    icon: "psychology",
    featured: true,
    supportedParams: [
      "model",
      "messages",
      "max_tokens",
      "temperature",
      "top_p",
      "top_k",
      "stop_sequences",
      "stream",
      "system",
      "metadata",
      "tools",
      "tool_choice",
      "thinking",
      "output_config",
      "cache_control",
      "service_tier",
      "container",
      "inference_geo",
    ],
  },
  "claude-sonnet-4-6": {
    family: "anthropic",
    inputPricePerMillion: 3,
    outputPricePerMillion: 15,
    cacheReadPricePerMillion: 0.3,
    cacheCreationPricePerMillion: 3.75,
    cacheCreation1hPricePerMillion: 6,
    pricingSourceModelId: "anthropic/claude-sonnet-4.6",
    pricingSourceUrl: "https://openrouter.ai/anthropic/claude-sonnet-4.6",
    pricingSourceCheckedAt: "2026-05-22",
    maxContextTokens: 1_000_000,
    description:
      "Best combination of speed and intelligence. Near-Opus performance at Sonnet pricing.",
    strongestFor:
      "Coding, computer use, balanced speed/quality tasks, best value for most use cases",
    releasedAt: "2026-02",
    displayName: "Claude Sonnet 4.6",
    version: "Sonnet 4.6",
    maxContext: "1M",
    maxOutput: "128K",
    latency: 16,
    icon: "psychology",
    featured: true,
    supportedParams: [
      "model",
      "messages",
      "max_tokens",
      "temperature",
      "top_p",
      "top_k",
      "stop_sequences",
      "stream",
      "system",
      "metadata",
      "tools",
      "tool_choice",
      "thinking",
      "output_config",
      "cache_control",
      "service_tier",
      "container",
      "inference_geo",
    ],
    routing: {
      modelId: "claude-sonnet-4-6",
      description:
        "Near-Opus performance at Sonnet pricing — best Anthropic value for most workloads (1M context)",
      strongestFor: [
        "text_generalist",
        "coding",
        "reasoning",
        "long_context",
        "high_safety",
      ],
      scores: {
        code: 0.92,
        math: 0.91,
        writing: 0.92,
        analysis: 0.92,
        qa_factual: 0.9,
        translation: 0.87,
        extraction: 0.91,
        classification: 0.89,
        creative: 0.91,
        reasoning: 0.93,
        long_context: 0.94,
        tool_use: 0.93,
        multimodal: 0.9,
        safety: 0.95,
      },
    },
  },
  "claude-haiku-4-5": {
    family: "anthropic",
    inputPricePerMillion: 1,
    outputPricePerMillion: 5,
    cacheReadPricePerMillion: 0.1,
    cacheCreationPricePerMillion: 1.25,
    cacheCreation1hPricePerMillion: 2,
    pricingSourceModelId: "anthropic/claude-haiku-4.5",
    pricingSourceUrl: "https://openrouter.ai/anthropic/claude-haiku-4.5",
    pricingSourceCheckedAt: "2026-05-22",
    maxContextTokens: 200_000,
    description:
      "Fastest model with near-frontier intelligence. Best cost-performance ratio.",
    strongestFor:
      "High-volume tasks, low-latency chat, sub-agent orchestration, rapid prototyping",
    releasedAt: "2025-10",
    displayName: "Claude Haiku 4.5",
    version: "Haiku 4.5",
    maxContext: "200K",
    maxOutput: "64K",
    latency: 8,
    icon: "psychology",
    supportedParams: [
      "model",
      "messages",
      "max_tokens",
      "temperature",
      "top_p",
      "top_k",
      "stop_sequences",
      "stream",
      "system",
      "metadata",
      "tools",
      "tool_choice",
      "thinking",
      "output_config",
      "cache_control",
      "service_tier",
      "container",
      "inference_geo",
    ],
    routing: {
      modelId: "claude-haiku-4-5",
      description:
        "Fastest Anthropic model — near-frontier intelligence at lowest cost (200K context)",
      strongestFor: ["text_generalist", "low_cost", "low_latency"],
      scores: {
        code: 0.84,
        math: 0.8,
        writing: 0.82,
        analysis: 0.82,
        qa_factual: 0.83,
        translation: 0.78,
        extraction: 0.83,
        classification: 0.82,
        creative: 0.8,
        reasoning: 0.82,
        long_context: 0.84,
        tool_use: 0.86,
        multimodal: 0.88,
        safety: 0.93,
      },
    },
  },
  "claude-sonnet-5": {
    family: "anthropic",
    inputPricePerMillion: 3,
    outputPricePerMillion: 15,
    cacheReadPricePerMillion: 0.3,
    cacheCreationPricePerMillion: 3.75,
    cacheCreation1hPricePerMillion: 6,
    pricingSourceModelId: "anthropic/claude-sonnet-5",
    pricingSourceUrl: "https://openrouter.ai/anthropic/claude-sonnet-5",
    pricingSourceCheckedAt: "2026-07-02",
    maxContextTokens: 1_000_000,
    description:
      "Next-generation Sonnet with enhanced reasoning and coding capabilities. Successor to Sonnet 4.6.",
    strongestFor:
      "Advanced coding, complex reasoning, balanced speed-quality, production workloads",
    releasedAt: "2026-06",
    displayName: "Claude Sonnet 5",
    version: "Sonnet 5",
    maxContext: "1M",
    maxOutput: "128K",
    latency: 16,
    icon: "psychology",
    featured: true,
    supportedParams: [
      "model",
      "messages",
      "max_tokens",
      "temperature",
      "top_p",
      "top_k",
      "stop_sequences",
      "stream",
      "system",
      "metadata",
      "tools",
      "tool_choice",
      "thinking",
      "output_config",
      "cache_control",
      "service_tier",
      "container",
      "inference_geo",
    ],
    routing: {
      modelId: "claude-sonnet-5",
      description:
        "Next-gen Sonnet — enhanced reasoning and coding with 1M context, best value for production workloads",
      strongestFor: [
        "text_generalist",
        "coding",
        "reasoning",
        "long_context",
        "high_safety",
      ],
      scores: {
        code: 0.94,
        math: 0.93,
        writing: 0.93,
        analysis: 0.94,
        qa_factual: 0.92,
        translation: 0.89,
        extraction: 0.93,
        classification: 0.91,
        creative: 0.92,
        reasoning: 0.95,
        long_context: 0.95,
        tool_use: 0.95,
        multimodal: 0.92,
        safety: 0.96,
      },
    },
  },
  "claude-sonnet-5-5": {
    family: "anthropic",
    inputPricePerMillion: 2,
    outputPricePerMillion: 10,
    cacheReadPricePerMillion: 0.1,
    cacheCreationPricePerMillion: 2.5,
    cacheCreation1hPricePerMillion: 4,
    pricingSourceModelId: "claude-sonnet-5-5",
    pricingSourceUrl:
      "https://platform.claude.com/docs/en/about-claude/pricing",
    pricingSourceCheckedAt: "2026-10-21",
    maxContextTokens: 1_000_000,
    description:
      "Anthropic's best combination of speed and intelligence. Successor to Sonnet 5.",
    strongestFor:
      "Coding, agentic workflows, balanced speed-quality, production workloads",
    releasedAt: "2026-09",
    displayName: "Claude Sonnet 5.5",
    version: "Sonnet 5.5",
    maxContext: "1M",
    maxOutput: "128K",
    latency: 16,
    dataCutoff: "2026-06",
    icon: "psychology",
    featured: true,
    supportedParams: [
      "model",
      "messages",
      "max_tokens",
      "temperature",
      "top_p",
      "top_k",
      "stop_sequences",
      "stream",
      "system",
      "metadata",
      "tools",
      "tool_choice",
      "thinking",
      "output_config",
      "cache_control",
      "service_tier",
      "container",
      "inference_geo",
    ],
  },
  "claude-fable-5": {
    family: "anthropic",
    inputPricePerMillion: 10,
    outputPricePerMillion: 50,
    cacheReadPricePerMillion: 1,
    cacheCreationPricePerMillion: 12.5,
    cacheCreation1hPricePerMillion: 20,
    pricingSourceModelId: "anthropic/claude-fable-5",
    pricingSourceUrl: "https://openrouter.ai/anthropic/claude-fable-5",
    pricingSourceCheckedAt: "2026-07-02",
    maxContextTokens: 1_000_000,
    description:
      "Anthropic's most advanced frontier model for the most demanding reasoning, coding, and agentic tasks.",
    strongestFor:
      "Frontier reasoning, complex agentic coding, long-horizon tasks, highest-quality professional work",
    releasedAt: "2026-06",
    displayName: "Claude Fable 5",
    version: "Fable 5",
    maxContext: "1M",
    maxOutput: "128K",
    latency: 22,
    dataCutoff: "2026-01",
    icon: "psychology",
    featured: true,
    supportedParams: [
      "model",
      "messages",
      "max_tokens",
      "temperature",
      "top_p",
      "top_k",
      "stop_sequences",
      "stream",
      "system",
      "metadata",
      "tools",
      "tool_choice",
      "thinking",
      "output_config",
      "cache_control",
      "service_tier",
      "container",
      "inference_geo",
    ],
    routing: {
      modelId: "claude-fable-5",
      description:
        "Anthropic's most advanced frontier model — top-tier reasoning, agentic coding, and long-context work (1M context)",
      strongestFor: [
        "text_generalist",
        "coding",
        "reasoning",
        "math",
        "long_context",
        "tool_use",
        "high_safety",
      ],
      scores: {
        code: 0.98,
        math: 0.98,
        writing: 0.96,
        analysis: 0.98,
        qa_factual: 0.96,
        translation: 0.93,
        extraction: 0.97,
        classification: 0.95,
        creative: 0.96,
        reasoning: 0.99,
        long_context: 0.98,
        tool_use: 0.98,
        multimodal: 0.95,
        safety: 0.98,
      },
    },
  },
  "claude-fable-5-1": {
    family: "anthropic",
    inputPricePerMillion: 10,
    outputPricePerMillion: 50,
    cacheReadPricePerMillion: 0.25,
    cacheCreationPricePerMillion: 12.5,
    cacheCreation1hPricePerMillion: 20,
    pricingSourceModelId: "anthropic/claude-fable-5.1",
    pricingSourceUrl: "https://openrouter.ai/anthropic/claude-fable-5.1",
    pricingSourceCheckedAt: "2026-09-02",
    pricingNotes:
      "Cache read rate is $0.25/M (discounted vs Fable 5's $1.00/M). Standard cache write is $12.50/M (1.25x base input) and 1-hour write is $20.00/M (2x base input).",
    maxContextTokens: 1_000_000,
    description:
      "Anthropic's updated frontier model improving across the board on agentic coding, long-running agent workflows, and knowledge work. More concise in plans and summaries than Fable 5.",
    strongestFor:
      "Frontier reasoning, long code refactors, front-end and visual code generation, agentic coding, finance and analysis",
    releasedAt: "2026-09-01",
    displayName: "Claude Fable 5.1",
    version: "Fable 5.1",
    maxContext: "1M",
    maxOutput: "128K",
    latency: 22,
    icon: "psychology",
    featured: true,
    supportedParams: [
      "model",
      "messages",
      "max_tokens",
      "temperature",
      "top_p",
      "top_k",
      "stop_sequences",
      "stream",
      "system",
      "metadata",
      "tools",
      "tool_choice",
      "thinking",
      "output_config",
      "cache_control",
      "service_tier",
      "container",
      "inference_geo",
    ],
    routing: {
      modelId: "claude-fable-5-1",
      description:
        "Anthropic's updated flagship frontier model — top-tier agentic coding, long-running workflows, concise planning, and long context (1M context)",
      strongestFor: [
        "text_generalist",
        "coding",
        "reasoning",
        "math",
        "long_context",
        "tool_use",
        "high_safety",
      ],
      scores: {
        code: 0.99,
        math: 0.98,
        writing: 0.96,
        analysis: 0.98,
        qa_factual: 0.96,
        translation: 0.93,
        extraction: 0.97,
        classification: 0.95,
        creative: 0.96,
        reasoning: 0.99,
        long_context: 0.98,
        tool_use: 0.99,
        multimodal: 0.96,
        safety: 0.98,
      },
    },
  },

  "claude-opus-5": {
    family: "anthropic",
    inputPricePerMillion: 5,
    outputPricePerMillion: 25,
    cacheReadPricePerMillion: 0.5,
    cacheCreationPricePerMillion: 6.25,
    cacheCreation1hPricePerMillion: 10,
    pricingSourceModelId: "claude-opus-5",
    pricingSourceUrl:
      "https://platform.claude.com/docs/en/about-claude/pricing",
    pricingSourceCheckedAt: "2026-07-29",
    maxContextTokens: 1_000_000,
    description:
      "Anthropic's flagship model for complex agentic coding and enterprise work. Tops Fable 5 on most benchmarks at half the price.",
    strongestFor:
      "Complex agentic coding, enterprise work, long-horizon multi-step tasks",
    releasedAt: "2026-07-24",
    displayName: "Claude Opus 5",
    version: "Opus 5",
    maxContext: "1M",
    maxOutput: "128K",
    latency: 20,
    dataCutoff: "2026-05",
    icon: "psychology",
    featured: true,
    supportedParams: [
      "model",
      "messages",
      "max_tokens",
      "temperature",
      "top_p",
      "top_k",
      "stop_sequences",
      "stream",
      "system",
      "metadata",
      "tools",
      "tool_choice",
      "thinking",
      "output_config",
      "cache_control",
      "service_tier",
      "container",
      "inference_geo",
    ],
    routing: {
      modelId: "claude-opus-5",
      description:
        "Anthropic's flagship for complex agentic coding and enterprise work — tops Fable 5 on most benchmarks (1M context)",
      strongestFor: [
        "text_generalist",
        "coding",
        "reasoning",
        "math",
        "long_context",
        "tool_use",
        "high_safety",
      ],
      scores: {
        code: 0.98,
        math: 0.97,
        writing: 0.95,
        analysis: 0.97,
        qa_factual: 0.95,
        translation: 0.92,
        extraction: 0.96,
        classification: 0.94,
        creative: 0.95,
        reasoning: 0.98,
        long_context: 0.98,
        tool_use: 0.98,
        multimodal: 0.94,
        safety: 0.97,
      },
    },
  },
  "claude-opus-5-5": {
    family: "anthropic",
    inputPricePerMillion: 4,
    outputPricePerMillion: 20,
    cacheReadPricePerMillion: 0.2,
    cacheCreationPricePerMillion: 5,
    cacheCreation1hPricePerMillion: 8,
    pricingSourceModelId: "claude-opus-5-5",
    pricingSourceUrl:
      "https://platform.claude.com/docs/en/about-claude/pricing",
    pricingSourceCheckedAt: "2026-10-21",
    maxContextTokens: 1_000_000,
    description:
      "Anthropic's model for long-running agentic coding and knowledge work. Successor to Opus 5.",
    strongestFor:
      "Long-running agentic coding, knowledge work, complex multi-step tasks",
    releasedAt: "2026-09",
    displayName: "Claude Opus 5.5",
    version: "Opus 5.5",
    maxContext: "1M",
    maxOutput: "128K",
    latency: 20,
    dataCutoff: "2026-06",
    icon: "psychology",
    featured: true,
    supportedParams: [
      "model",
      "messages",
      "max_tokens",
      "temperature",
      "top_p",
      "top_k",
      "stop_sequences",
      "stream",
      "system",
      "metadata",
      "tools",
      "tool_choice",
      "thinking",
      "output_config",
      "cache_control",
      "service_tier",
      "container",
      "inference_geo",
    ],
  },
  "claude-haiku-5-5": {
    family: "anthropic",
    inputPricePerMillion: 0.1,
    outputPricePerMillion: 0.5,
    cacheReadPricePerMillion: 0.01,
    cacheCreationPricePerMillion: 0.125,
    cacheCreation1hPricePerMillion: 0.2,
    pricingSourceModelId: "claude-haiku-5-5",
    pricingSourceUrl:
      "https://platform.claude.com/docs/en/about-claude/pricing",
    pricingSourceCheckedAt: "2026-10-21",
    pricingNotes:
      "Base tier (prompts up to 100K tokens) shown: $0.10/$0.50. Prompts over 100K tokens bill the whole request at $0.50 input / $2.50 output / $0.05 cache read / $0.625 cache write / $1 1h cache write; the catalog has no prompt-length tiering.",
    maxContextTokens: 1_000_000,
    description:
      "Anthropic's fastest model for high-volume, latency-sensitive tasks such as classification, extraction, and routing.",
    strongestFor:
      "Classification, extraction, routing, high-volume low-latency workloads",
    releasedAt: "2026-10",
    displayName: "Claude Haiku 5.5",
    version: "Haiku 5.5",
    maxContext: "1M",
    maxOutput: "128K",
    latency: 8,
    dataCutoff: "2026-06",
    icon: "psychology",
    featured: true,
    supportedParams: [
      "model",
      "messages",
      "max_tokens",
      "temperature",
      "top_p",
      "top_k",
      "stop_sequences",
      "stream",
      "system",
      "metadata",
      "tools",
      "tool_choice",
      "thinking",
      "output_config",
      "cache_control",
      "service_tier",
      "container",
      "inference_geo",
    ],
  },

  // ========================================
  // OPENAI MODELS
  // Verified against OpenRouter model pricing (checked 2026-05-22).
  // ========================================
  "gpt-5.5": {
    family: "openai",
    inputPricePerMillion: 5,
    outputPricePerMillion: 30,
    cacheReadPricePerMillion: 0.5,
    cacheCreationPricePerMillion: 5,
    pricingSourceModelId: "openai/gpt-5.5",
    pricingSourceUrl: "https://openrouter.ai/openai/gpt-5.5",
    pricingSourceCheckedAt: "2026-05-22",
    pricingNotes:
      "OpenRouter does not publish a separate input_cache_write price; cache creation is priced as normal input.",
    maxContextTokens: 1_050_000,
    description:
      "OpenAI's latest frontier reasoning model with enhanced capabilities and 1M context window.",
    strongestFor:
      "Advanced reasoning, complex problem-solving, highest intelligence tasks, extended context",
    releasedAt: "2026-04",
    displayName: "GPT-5.5",
    version: "v5.5",
    maxContext: "1.05M",
    maxOutput: "128K",
    latency: 20,
    dataCutoff: "2025-12",
    icon: "smart_toy",
    featured: true,
    supportedParams: [
      "model",
      "messages",
      "max_tokens",
      "max_completion_tokens",
      "temperature",
      "top_p",
      "n",
      "stream",
      "stop",
      "presence_penalty",
      "frequency_penalty",
      "logit_bias",
      "user",
      "tools",
      "tool_choice",
      "parallel_tool_calls",
      "response_format",
      "seed",
      "logprobs",
      "top_logprobs",
    ],
    routing: {
      modelId: "gpt-5.5",
      description:
        "OpenAI's latest frontier reasoning model — 1M context, enhanced capabilities for complex problem-solving and extended reasoning",
      strongestFor: [
        "text_generalist",
        "coding",
        "reasoning",
        "math",
        "long_context",
        "tool_use",
      ],
      scores: {
        code: 0.96,
        math: 0.97,
        writing: 0.94,
        analysis: 0.96,
        qa_factual: 0.94,
        translation: 0.9,
        extraction: 0.94,
        classification: 0.92,
        creative: 0.93,
        reasoning: 0.97,
        long_context: 0.98,
        tool_use: 0.99,
        multimodal: 0.96,
        safety: 0.94,
      },
    },
  },
  "gpt-5.6": {
    family: "openai",
    inputPricePerMillion: 4,
    outputPricePerMillion: 20,
    cacheReadPricePerMillion: 0.4,
    cacheCreationPricePerMillion: 5,
    pricingSourceModelId: "gpt-5.6-sol",
    pricingSourceUrl: "https://developers.openai.com/api/docs/models/gpt-5.6",
    pricingSourceCheckedAt: "2026-08-26",
    pricingNotes:
      "The gpt-5.6 alias routes requests to GPT-5.6 Sol; pricing matches gpt-5.6-sol. Reflects the promotional price cut effective 2026-08-22 (runs through 2026-11-21). Cache writes are 1.25x the standard input rate.",
    maxContextTokens: 1_050_000,
    description:
      "Alias for GPT-5.6 Sol, OpenAI's frontier reasoning and coding model with a 1.05M context window.",
    strongestFor:
      "Advanced reasoning, terminal-based coding, complex agentic workflows, extended context",
    releasedAt: "2026-06",
    displayName: "GPT-5.6",
    version: "v5.6",
    maxContext: "1.05M",
    maxOutput: "128K",
    latency: 22,
    dataCutoff: "2026-02",
    icon: "smart_toy",
    featured: true,
    supportedParams: [
      "model",
      "messages",
      "max_tokens",
      "max_completion_tokens",
      "temperature",
      "top_p",
      "n",
      "stream",
      "stop",
      "presence_penalty",
      "frequency_penalty",
      "logit_bias",
      "user",
      "tools",
      "tool_choice",
      "parallel_tool_calls",
      "response_format",
      "seed",
      "logprobs",
      "top_logprobs",
    ],
  },
  "gpt-5.6-sol": {
    family: "openai",
    inputPricePerMillion: 4,
    outputPricePerMillion: 20,
    cacheReadPricePerMillion: 0.4,
    cacheCreationPricePerMillion: 5,
    pricingSourceModelId: "gpt-5.6-sol",
    pricingSourceUrl:
      "https://developers.openai.com/api/docs/models/gpt-5.6-sol",
    pricingSourceCheckedAt: "2026-08-26",
    pricingNotes:
      "Reflects the promotional price cut effective 2026-08-22 (runs through 2026-11-21). Prompts with >272K input tokens are priced at 2x input and 1.5x output for the full request. Cache writes are 1.25x the standard input rate.",
    maxContextTokens: 1_050_000,
    description:
      "OpenAI's frontier tier of the GPT-5.6 family — advanced reasoning and coding, with 'max' reasoning effort and 'ultra' subagent modes.",
    strongestFor:
      "Advanced reasoning, terminal-based coding, cybersecurity evaluation, complex agentic workflows, extended context",
    releasedAt: "2026-06",
    displayName: "GPT-5.6 Sol",
    version: "5.6-sol",
    maxContext: "1.05M",
    maxOutput: "128K",
    latency: 22,
    dataCutoff: "2026-02",
    icon: "smart_toy",
    featured: true,
    supportedParams: [
      "model",
      "messages",
      "max_tokens",
      "max_completion_tokens",
      "temperature",
      "top_p",
      "n",
      "stream",
      "stop",
      "presence_penalty",
      "frequency_penalty",
      "logit_bias",
      "user",
      "tools",
      "tool_choice",
      "parallel_tool_calls",
      "response_format",
      "seed",
      "logprobs",
      "top_logprobs",
    ],
    routing: {
      modelId: "gpt-5.6-sol",
      description:
        "OpenAI frontier tier — state-of-the-art coding and reasoning, 1M context, 'max' effort and 'ultra' subagent modes",
      strongestFor: [
        "text_generalist",
        "coding",
        "reasoning",
        "math",
        "long_context",
        "tool_use",
      ],
      scores: {
        code: 0.98,
        math: 0.98,
        writing: 0.95,
        analysis: 0.97,
        qa_factual: 0.95,
        translation: 0.91,
        extraction: 0.95,
        classification: 0.93,
        creative: 0.94,
        reasoning: 0.98,
        long_context: 0.98,
        tool_use: 0.99,
        multimodal: 0.96,
        safety: 0.94,
      },
    },
  },
  "gpt-5.6-terra": {
    family: "openai",
    inputPricePerMillion: 2,
    outputPricePerMillion: 12,
    cacheReadPricePerMillion: 0.2,
    cacheCreationPricePerMillion: 2.5,
    pricingSourceModelId: "gpt-5.6-terra",
    pricingSourceUrl:
      "https://developers.openai.com/api/docs/models/gpt-5.6-terra",
    pricingSourceCheckedAt: "2026-08-26",
    pricingNotes:
      "Reflects the promotional price cut effective 2026-08-22 (runs through 2026-11-21). Prompts with >272K input tokens are priced at 2x input and 1.5x output for the full request. Cache writes are 1.25x the standard input rate.",
    maxContextTokens: 1_050_000,
    description:
      "Balanced tier of the GPT-5.6 family — GPT-5.5-competitive performance at roughly half the cost.",
    strongestFor:
      "Cost-effective reasoning, coding, and long-context tasks with strong general performance",
    releasedAt: "2026-06",
    displayName: "GPT-5.6 Terra",
    version: "5.6-terra",
    maxContext: "1.05M",
    maxOutput: "128K",
    latency: 18,
    dataCutoff: "2026-02",
    icon: "smart_toy",
    featured: true,
    supportedParams: [
      "model",
      "messages",
      "max_tokens",
      "max_completion_tokens",
      "temperature",
      "top_p",
      "n",
      "stream",
      "stop",
      "presence_penalty",
      "frequency_penalty",
      "logit_bias",
      "user",
      "tools",
      "tool_choice",
      "parallel_tool_calls",
      "response_format",
      "seed",
      "logprobs",
      "top_logprobs",
    ],
    routing: {
      modelId: "gpt-5.6-terra",
      description:
        "OpenAI balanced tier — GPT-5.5-competitive performance at lower cost, 1M context",
      strongestFor: [
        "text_generalist",
        "coding",
        "reasoning",
        "long_context",
        "tool_use",
      ],
      scores: {
        code: 0.93,
        math: 0.94,
        writing: 0.92,
        analysis: 0.93,
        qa_factual: 0.92,
        translation: 0.88,
        extraction: 0.91,
        classification: 0.9,
        creative: 0.9,
        reasoning: 0.94,
        long_context: 0.96,
        tool_use: 0.96,
        multimodal: 0.93,
        safety: 0.92,
      },
    },
  },
  "gpt-5.6-luna": {
    family: "openai",
    inputPricePerMillion: 0.2,
    outputPricePerMillion: 1.2,
    cacheReadPricePerMillion: 0.02,
    cacheCreationPricePerMillion: 0.25,
    pricingSourceModelId: "gpt-5.6-luna",
    pricingSourceUrl:
      "https://developers.openai.com/api/docs/models/gpt-5.6-luna",
    pricingSourceCheckedAt: "2026-08-26",
    pricingNotes:
      "Reflects the promotional price cut effective 2026-08-22 (runs through 2026-11-21). Prompts with >272K input tokens are priced at 2x input and 1.5x output for the full request. Cache writes are 1.25x the standard input rate.",
    maxContextTokens: 1_050_000,
    description:
      "Cost-optimized tier of the GPT-5.6 family for high-volume workloads, positioned above GPT-5.4 nano.",
    strongestFor:
      "High-volume workloads, fast responses, cost-sensitive tasks with extended context",
    releasedAt: "2026-06",
    displayName: "GPT-5.6 Luna",
    version: "5.6-luna",
    maxContext: "1.05M",
    maxOutput: "128K",
    latency: 10,
    dataCutoff: "2026-02",
    icon: "smart_toy",
    supportedParams: [
      "model",
      "messages",
      "max_tokens",
      "max_completion_tokens",
      "temperature",
      "top_p",
      "n",
      "stream",
      "stop",
      "presence_penalty",
      "frequency_penalty",
      "logit_bias",
      "user",
      "tools",
      "tool_choice",
      "parallel_tool_calls",
      "response_format",
      "seed",
      "logprobs",
      "top_logprobs",
    ],
    routing: {
      modelId: "gpt-5.6-luna",
      description:
        "OpenAI cost-optimized tier — fast and affordable for high-volume workloads, 1M context",
      strongestFor: [
        "text_generalist",
        "long_context",
        "low_cost",
        "coding",
      ],
      scores: {
        code: 0.87,
        math: 0.88,
        writing: 0.86,
        analysis: 0.87,
        qa_factual: 0.86,
        translation: 0.83,
        extraction: 0.86,
        classification: 0.85,
        creative: 0.84,
        reasoning: 0.88,
        long_context: 0.92,
        tool_use: 0.89,
        multimodal: 0.87,
        safety: 0.91,
      },
    },
  },
  "gpt-5.4": {
    family: "openai",
    inputPricePerMillion: 2.5,
    outputPricePerMillion: 15,
    cacheReadPricePerMillion: 0.25,
    cacheCreationPricePerMillion: 2.5,
    pricingSourceModelId: "openai/gpt-5.4",
    pricingSourceUrl: "https://openrouter.ai/openai/gpt-5.4",
    pricingSourceCheckedAt: "2026-05-22",
    pricingNotes:
      "OpenRouter does not publish a separate input_cache_write price; cache creation is priced as normal input.",
    maxContextTokens: 1_050_000,
    description:
      "OpenAI's most capable reasoning model with massive 1.05M context window.",
    strongestFor:
      "Extended reasoning, complex problem-solving, highest intelligence tasks",
    releasedAt: "2026-03",
    displayName: "GPT-5.4",
    version: "v5.4",
    maxContext: "1.05M",
    maxOutput: "128K",
    latency: 18,
    icon: "smart_toy",
    featured: true,
    supportedParams: [
      "model",
      "messages",
      "max_tokens",
      "max_completion_tokens",
      "temperature",
      "top_p",
      "n",
      "stream",
      "stop",
      "presence_penalty",
      "frequency_penalty",
      "logit_bias",
      "user",
      "tools",
      "tool_choice",
      "parallel_tool_calls",
      "response_format",
      "seed",
      "logprobs",
      "top_logprobs",
    ],
    routing: {
      modelId: "gpt-5.4",
      description:
        "OpenAI frontier reasoning model — ~1M context, state-of-the-art computer use and professional knowledge work",
      strongestFor: [
        "text_generalist",
        "coding",
        "reasoning",
        "math",
        "long_context",
        "tool_use",
      ],
      scores: {
        code: 0.94,
        math: 0.95,
        writing: 0.93,
        analysis: 0.95,
        qa_factual: 0.93,
        translation: 0.89,
        extraction: 0.93,
        classification: 0.91,
        creative: 0.92,
        reasoning: 0.96,
        long_context: 0.97,
        tool_use: 0.98,
        multimodal: 0.95,
        safety: 0.93,
      },
    },
  },
  "gpt-4.1-mini": {
    family: "openai",
    inputPricePerMillion: 0.4,
    outputPricePerMillion: 1.6,
    cacheReadPricePerMillion: 0.1,
    cacheCreationPricePerMillion: 0.4,
    pricingSourceModelId: "openai/gpt-4.1-mini",
    pricingSourceUrl: "https://openrouter.ai/openai/gpt-4.1-mini",
    pricingSourceCheckedAt: "2026-05-22",
    pricingNotes:
      "OpenRouter does not publish a separate input_cache_write price; cache creation is priced as normal input.",
    maxContextTokens: 1_047_576,
    description:
      "Mid-sized model with performance competitive with GPT-4o at lower latency and cost. 1M context window with strong coding and vision.",
    strongestFor:
      "Interactive applications, cost-sensitive coding, vision understanding",
    releasedAt: "2025-04",
    displayName: "GPT-4.1 Mini",
    version: "4.1-mini",
    maxContext: "1.05M",
    maxOutput: "33K",
    latency: 8,
    dataCutoff: "2024-06",
    icon: "smart_toy",
    supportedParams: [
      "model",
      "messages",
      "max_tokens",
      "max_completion_tokens",
      "temperature",
      "top_p",
      "n",
      "stream",
      "stop",
      "presence_penalty",
      "frequency_penalty",
      "logit_bias",
      "user",
      "tools",
      "tool_choice",
      "parallel_tool_calls",
      "response_format",
      "seed",
      "logprobs",
      "top_logprobs",
    ],
  },
  "gpt-4o-mini": {
    family: "openai",
    inputPricePerMillion: 0.15,
    outputPricePerMillion: 0.6,
    cacheReadPricePerMillion: 0.075,
    cacheCreationPricePerMillion: 0.15,
    pricingSourceModelId: "openai/gpt-4o-mini",
    pricingSourceUrl: "https://openrouter.ai/openai/gpt-4o-mini",
    pricingSourceCheckedAt: "2026-05-22",
    pricingNotes:
      "OpenRouter does not publish a separate input_cache_write price; cache creation is priced as normal input.",
    maxContextTokens: 128_000,
    description:
      "Ultra-affordable small model with vision — best for high-volume, cost-sensitive tasks.",
    strongestFor:
      "High-volume tasks, cost optimization, fast inference, simple chat and vision",
    releasedAt: "2024-07",
    displayName: "GPT-4o-mini",
    version: "4o-mini",
    maxContext: "128K",
    maxOutput: "16K",
    latency: 6,
    dataCutoff: "2023-10",
    icon: "smart_toy",
    supportedParams: [
      "model",
      "messages",
      "max_tokens",
      "max_completion_tokens",
      "temperature",
      "top_p",
      "n",
      "stream",
      "stop",
      "presence_penalty",
      "frequency_penalty",
      "logit_bias",
      "user",
      "tools",
      "tool_choice",
      "parallel_tool_calls",
      "response_format",
      "seed",
      "logprobs",
      "top_logprobs",
    ],
    routing: {
      modelId: "gpt-4o-mini",
      description:
        "Ultra-affordable small model with vision — best for high-volume, cost-sensitive tasks with fast inference",
      strongestFor: [
        "low_cost",
        "low_latency",
        "text_generalist",
        "image_understanding",
      ],
      scores: {
        code: 0.82,
        math: 0.79,
        writing: 0.81,
        analysis: 0.8,
        qa_factual: 0.82,
        translation: 0.78,
        extraction: 0.81,
        classification: 0.8,
        creative: 0.79,
        reasoning: 0.8,
        long_context: 0.82,
        tool_use: 0.84,
        multimodal: 0.87,
        safety: 0.91,
      },
    },
  },
  "gpt-4.1": {
    family: "openai",
    inputPricePerMillion: 2,
    outputPricePerMillion: 8,
    cacheReadPricePerMillion: 0.5,
    cacheCreationPricePerMillion: 2,
    pricingSourceModelId: "openai/gpt-4.1",
    pricingSourceUrl: "https://developers.openai.com/api/docs/models/gpt-4.1",
    pricingSourceCheckedAt: "2026-08-26",
    maxContextTokens: 1_000_000,
    description:
      "Production workhorse model with 1M context. Good for coding and complex tasks. Retired from ChatGPT but available via API.",
    strongestFor:
      "Production coding, 1M context applications, balanced cost-performance",
    releasedAt: "2025-12",
    displayName: "GPT-4.1",
    version: "4.1",
    maxContext: "1M",
    maxOutput: "128K",
    latency: 18,
    icon: "smart_toy",
    supportedParams: [
      "model",
      "messages",
      "max_tokens",
      "max_completion_tokens",
      "temperature",
      "top_p",
      "n",
      "stream",
      "stop",
      "presence_penalty",
      "frequency_penalty",
      "logit_bias",
      "user",
      "tools",
      "tool_choice",
      "parallel_tool_calls",
      "response_format",
      "seed",
      "logprobs",
      "top_logprobs",
    ],
    routing: {
      modelId: "gpt-4.1",
      description:
        "Production workhorse with 1M context — balanced cost-performance for coding and complex tasks",
      strongestFor: ["text_generalist", "coding", "long_context"],
      scores: {
        code: 0.88,
        math: 0.86,
        writing: 0.87,
        analysis: 0.87,
        qa_factual: 0.86,
        translation: 0.84,
        extraction: 0.87,
        classification: 0.85,
        creative: 0.86,
        reasoning: 0.87,
        long_context: 0.9,
        tool_use: 0.89,
        multimodal: 0.85,
        safety: 0.92,
      },
    },
  },
  "gpt-4.1-nano": {
    family: "openai",
    inputPricePerMillion: 0.1,
    outputPricePerMillion: 0.4,
    cacheReadPricePerMillion: 0.025,
    cacheCreationPricePerMillion: 0.1,
    pricingSourceModelId: "openai/gpt-4.1-nano",
    pricingSourceUrl:
      "https://developers.openai.com/api/docs/models/gpt-4.1-nano",
    pricingSourceCheckedAt: "2026-08-26",
    pricingNotes:
      "Context window corrected from a stale 128K (likely confused with gpt-4o-mini) to the actual 1.05M shared by the rest of the GPT-4.1 family. Scheduled for deprecation 2026-10-23; replacement is gpt-5.6-luna.",
    maxContextTokens: 1_050_000,
    description:
      "One of the cheapest capable models available anywhere. OpenAI's answer to budget-conscious developers.",
    strongestFor:
      "Ultra-low cost tasks, high-volume processing, simple queries",
    releasedAt: "2026-01",
    displayName: "GPT-4.1 Nano",
    version: "4.1-nano",
    maxContext: "1.05M",
    maxOutput: "16K",
    latency: 5,
    icon: "smart_toy",
    supportedParams: [
      "model",
      "messages",
      "max_tokens",
      "max_completion_tokens",
      "temperature",
      "top_p",
      "n",
      "stream",
      "stop",
      "presence_penalty",
      "frequency_penalty",
      "logit_bias",
      "user",
      "tools",
      "tool_choice",
      "parallel_tool_calls",
      "response_format",
      "seed",
      "logprobs",
      "top_logprobs",
    ],
    routing: {
      modelId: "gpt-4.1-nano",
      description:
        "Ultra-low cost capable model — one of the cheapest options available with decent quality",
      strongestFor: ["low_cost", "low_latency", "text_generalist"],
      scores: {
        code: 0.78,
        math: 0.75,
        writing: 0.77,
        analysis: 0.76,
        qa_factual: 0.78,
        translation: 0.74,
        extraction: 0.77,
        classification: 0.76,
        creative: 0.75,
        reasoning: 0.76,
        long_context: 0.78,
        tool_use: 0.8,
        multimodal: 0.82,
        safety: 0.9,
      },
    },
  },
  "gpt-5.4-mini": {
    family: "openai",
    inputPricePerMillion: 0.75,
    outputPricePerMillion: 4.5,
    cacheReadPricePerMillion: 0.075,
    cacheCreationPricePerMillion: 0.75,
    pricingSourceModelId: "openai/gpt-5.4-mini",
    pricingSourceUrl: "https://developers.openai.com/api/docs/models/gpt-5.4-mini",
    pricingSourceCheckedAt: "2026-08-26",
    pricingNotes:
      "Price and context window were both stale/incorrect (previously $0.2/$0.8 and a claimed 1.05M context, roughly 3.75x understated and mismatched with the actual 400K window — likely mixed up with a different tier). Max 272K input / 128K output tokens within the 400K window. OpenAI does not publish a separate cache-write price; cache creation is priced as normal input.",
    maxContextTokens: 400_000,
    description:
      "Compact reasoning model with extended 400K context. Balanced cost-performance for reasoning tasks.",
    strongestFor:
      "Cost-effective reasoning, extended context processing, balanced intelligence-cost ratio",
    releasedAt: "2026-06",
    displayName: "GPT-5.4 Mini",
    version: "5.4-mini",
    maxContext: "400K",
    maxOutput: "128K",
    latency: 10,
    icon: "smart_toy",
    featured: true,
    supportedParams: [
      "model",
      "messages",
      "max_tokens",
      "max_completion_tokens",
      "temperature",
      "top_p",
      "n",
      "stream",
      "stop",
      "presence_penalty",
      "frequency_penalty",
      "logit_bias",
      "user",
      "tools",
      "tool_choice",
      "parallel_tool_calls",
      "response_format",
      "seed",
      "logprobs",
      "top_logprobs",
    ],
    routing: {
      modelId: "gpt-5.4-mini",
      description:
        "Compact reasoning model with 1M+ context — balanced cost-performance for reasoning and extended context",
      strongestFor: [
        "text_generalist",
        "reasoning",
        "long_context",
        "low_cost",
        "coding",
      ],
      scores: {
        code: 0.86,
        math: 0.88,
        writing: 0.85,
        analysis: 0.87,
        qa_factual: 0.86,
        translation: 0.83,
        extraction: 0.86,
        classification: 0.85,
        creative: 0.84,
        reasoning: 0.89,
        long_context: 0.92,
        tool_use: 0.88,
        multimodal: 0.87,
        safety: 0.91,
      },
    },
  },
  // ========================================
  // GOOGLE GEMINI MODELS
  // Verified against OpenRouter model pricing (checked 2026-05-22).
  // ========================================
  "gemini-3.1-pro": {
    family: "google",
    inputPricePerMillion: 2,
    outputPricePerMillion: 12,
    cacheReadPricePerMillion: 0.2,
    cacheCreationPricePerMillion: 0.375,
    pricingSourceModelId: "google/gemini-3.1-pro-preview",
    pricingSourceUrl: "https://openrouter.ai/google/gemini-3.1-pro-preview",
    pricingSourceCheckedAt: "2026-05-22",
    pricingNotes:
      "Local alias maps to OpenRouter's Gemini 3.1 Pro Preview model id.",
    maxContextTokens: 1_048_576,
    description:
      "Most capable Gemini model. Optimized for software engineering and agentic workflows.",
    strongestFor:
      "Complex problem-solving, agentic coding, software engineering, deep reasoning",
    releasedAt: "2026-02",
    displayName: "Gemini 3.1 Pro Preview",
    version: "3.1 Pro",
    maxContext: "1.05M",
    maxOutput: "66K",
    latency: 22,
    icon: "view_in_ar",
    featured: true,
    supportedParams: [
      "model",
      "contents",
      "temperature",
      "top_p",
      "top_k",
      "max_output_tokens",
      "candidate_count",
      "stop_sequences",
      "response_mime_type",
      "response_schema",
      "safety_settings",
      "system_instruction",
      "tools",
      "tool_config",
      "cached_content",
      "service_tier",
    ],
    routing: {
      modelId: "gemini-3.1-pro",
      description:
        "Most capable Gemini — frontier reasoning, coding, and multimodal with 1M context and strong agentic performance",
      strongestFor: [
        "text_generalist",
        "coding",
        "reasoning",
        "math",
        "image_understanding",
        "document_understanding",
        "long_context",
        "tool_use",
      ],
      scores: {
        code: 0.93,
        math: 0.94,
        writing: 0.91,
        analysis: 0.94,
        qa_factual: 0.91,
        translation: 0.88,
        extraction: 0.92,
        classification: 0.9,
        creative: 0.9,
        reasoning: 0.95,
        long_context: 0.96,
        tool_use: 0.95,
        multimodal: 0.96,
        safety: 0.92,
      },
    },
  },
  "gemini-3.1-flash-lite": {
    family: "google",
    inputPricePerMillion: 0.25,
    outputPricePerMillion: 1.5,
    cacheReadPricePerMillion: 0.025,
    cacheCreationPricePerMillion: 0.08333333333333334,
    pricingSourceModelId: "google/gemini-3.1-flash-lite",
    pricingSourceUrl: "https://openrouter.ai/google/gemini-3.1-flash-lite",
    pricingSourceCheckedAt: "2026-05-22",
    maxContextTokens: 1_048_576,
    description:
      "Google GA high-efficiency multimodal model optimized for low-latency, high-volume workloads. Half the cost of Gemini 3 Flash.",
    strongestFor:
      "High-volume tasks, lightweight agentic workflows, cost-sensitive multimodal applications",
    releasedAt: "2026-05",
    displayName: "Gemini 3.1 Flash Lite",
    version: "3.1 Flash Lite",
    maxContext: "1.05M",
    maxOutput: "66K",
    latency: 8,
    icon: "view_in_ar",
    supportedParams: [
      "model",
      "contents",
      "temperature",
      "top_p",
      "top_k",
      "max_output_tokens",
      "candidate_count",
      "stop_sequences",
      "response_mime_type",
      "response_schema",
      "safety_settings",
      "system_instruction",
      "tools",
      "tool_config",
      "cached_content",
      "service_tier",
    ],
  },
  "gemini-3-flash": {
    family: "google",
    inputPricePerMillion: 0.5,
    outputPricePerMillion: 3,
    cacheReadPricePerMillion: 0.05,
    cacheCreationPricePerMillion: 0.08333333333333334,
    pricingSourceModelId: "google/gemini-3-flash-preview",
    pricingSourceUrl: "https://openrouter.ai/google/gemini-3-flash-preview",
    pricingSourceCheckedAt: "2026-05-22",
    pricingNotes:
      "Local alias maps to OpenRouter's Gemini 3 Flash Preview model id.",
    maxContextTokens: 1_048_576,
    description:
      "Best multimodal understanding model. Frontier performance at lower cost. Supports computer use.",
    strongestFor:
      "Multimodal understanding, agentic workflows, computer use, vibe coding",
    releasedAt: "2025-12",
    displayName: "Gemini 3 Flash Preview",
    version: "3 Flash",
    maxContext: "1.05M",
    maxOutput: "66K",
    supportedParams: [
      "model",
      "contents",
      "temperature",
      "top_p",
      "top_k",
      "max_output_tokens",
      "candidate_count",
      "stop_sequences",
      "response_mime_type",
      "response_schema",
      "safety_settings",
      "system_instruction",
      "tools",
      "tool_config",
      "cached_content",
      "service_tier",
    ],
  },
  "gemini-2.5-pro": {
    family: "google",
    inputPricePerMillion: 1.25,
    outputPricePerMillion: 10,
    cacheReadPricePerMillion: 0.125,
    cacheCreationPricePerMillion: 0.375,
    pricingSourceModelId: "google/gemini-2.5-pro",
    pricingSourceUrl: "https://openrouter.ai/google/gemini-2.5-pro",
    pricingSourceCheckedAt: "2026-05-22",
    maxContextTokens: 1_048_576,
    description:
      "State-of-the-art thinking model for complex reasoning in code, math, and STEM.",
    strongestFor:
      "Complex reasoning, coding, math, STEM, long-context analysis",
    releasedAt: "2025-06",
    dataCutoff: "2025-01",
    displayName: "Gemini 2.5 Pro",
    version: "2.5 Pro",
    maxContext: "1.05M",
    maxOutput: "66K",
    supportedParams: [
      "model",
      "contents",
      "temperature",
      "top_p",
      "top_k",
      "max_output_tokens",
      "candidate_count",
      "stop_sequences",
      "response_mime_type",
      "response_schema",
      "safety_settings",
      "system_instruction",
      "tools",
      "tool_config",
      "cached_content",
      "service_tier",
    ],
  },
  "gemma-4-31b-it": {
    family: "google",
    inputPricePerMillion: 0.08,
    outputPricePerMillion: 0.35,
    cacheReadPricePerMillion: 0.01,
    cacheCreationPricePerMillion: 0.08,
    pricingSourceModelId: "google/gemma-4-31b-it",
    pricingSourceUrl: "https://openrouter.ai/google/gemma-4-31b-it",
    pricingSourceCheckedAt: "2026-08-26",
    pricingNotes:
      "OpenRouter now publishes an explicit cache-read discount ($0.01/M); cache creation still isn't published separately and is priced as normal input.",
    maxContextTokens: 262_144,
    description:
      "State-of-the-art thinking model for complex reasoning in code, math, and STEM.",
    strongestFor:
      "Complex reasoning, coding, math, STEM, long-context analysis",
    releasedAt: "2026-04",
    displayName: "Gemma 4 31B",
    version: "4 31B",
    maxContext: "262K",
    maxOutput: "16K",
    supportedParams: [
      "model",
      "contents",
      "temperature",
      "top_p",
      "top_k",
      "max_output_tokens",
      "candidate_count",
      "stop_sequences",
      "response_mime_type",
      "response_schema",
      "safety_settings",
      "system_instruction",
      "tools",
      "tool_config",
      "cached_content",
      "service_tier",
    ],
  },
  "gemini-2.5-flash": {
    family: "google",
    inputPricePerMillion: 0.3,
    outputPricePerMillion: 2.5,
    cacheReadPricePerMillion: 0.03,
    cacheCreationPricePerMillion: 0.08333333333333334,
    pricingSourceModelId: "google/gemini-2.5-flash",
    pricingSourceUrl: "https://openrouter.ai/google/gemini-2.5-flash",
    pricingSourceCheckedAt: "2026-05-22",
    maxContextTokens: 1_048_576,
    description:
      "Best price-performance model. Ideal for large-scale processing and agentic use cases.",
    strongestFor:
      "Price-performance balance, large-scale processing, low-latency reasoning",
    releasedAt: "2025-06",
    dataCutoff: "2025-01",
    displayName: "Gemini 2.5 Flash",
    version: "2.5 Flash",
    maxContext: "1.05M",
    maxOutput: "66K",
    supportedParams: [
      "model",
      "contents",
      "temperature",
      "top_p",
      "top_k",
      "max_output_tokens",
      "candidate_count",
      "stop_sequences",
      "response_mime_type",
      "response_schema",
      "safety_settings",
      "system_instruction",
      "tools",
      "tool_config",
      "cached_content",
      "service_tier",
    ],
  },
  "gemini-2.5-flash-lite": {
    family: "google",
    inputPricePerMillion: 0.1,
    outputPricePerMillion: 0.4,
    cacheReadPricePerMillion: 0.01,
    cacheCreationPricePerMillion: 0.08333333333333334,
    pricingSourceModelId: "google/gemini-2.5-flash-lite",
    pricingSourceUrl: "https://ai.google.dev/gemini-api/docs/pricing",
    pricingSourceCheckedAt: "2026-06-16",
    maxContextTokens: 1_048_576,
    description:
      "Ultra-low latency and cost efficiency model. 90% cheaper cache reads. Optimized for high-volume production workloads.",
    strongestFor:
      "High-volume tasks, classification, translation, intelligent routing, cost-sensitive workloads",
    releasedAt: "2026-05",
    dataCutoff: "2025-01",
    displayName: "Gemini 2.5 Flash Lite",
    version: "2.5 Flash Lite",
    maxContext: "1.05M",
    maxOutput: "66K",
    latency: 6,
    icon: "view_in_ar",
    supportedParams: [
      "model",
      "contents",
      "temperature",
      "top_p",
      "top_k",
      "max_output_tokens",
      "candidate_count",
      "stop_sequences",
      "response_mime_type",
      "response_schema",
      "safety_settings",
      "system_instruction",
      "tools",
      "tool_config",
      "cached_content",
      "service_tier",
    ],
    routing: {
      modelId: "gemini-2.5-flash-lite",
      description:
        "Ultra-efficient multimodal model — 90% cheaper cache reads, optimized for high-volume production tasks",
      strongestFor: [
        "low_cost",
        "low_latency",
        "text_generalist",
        "image_understanding",
      ],
      scores: {
        code: 0.79,
        math: 0.77,
        writing: 0.78,
        analysis: 0.78,
        qa_factual: 0.8,
        translation: 0.82,
        extraction: 0.8,
        classification: 0.83,
        creative: 0.76,
        reasoning: 0.78,
        long_context: 0.85,
        tool_use: 0.82,
        multimodal: 0.88,
        safety: 0.91,
      },
    },
  },
  "gemini-3.5-flash": {
    family: "google",
    inputPricePerMillion: 1.5,
    outputPricePerMillion: 9,
    cacheReadPricePerMillion: 0.15,
    cacheCreationPricePerMillion: 1,
    pricingSourceModelId: "google/gemini-3.5-flash",
    pricingSourceUrl: "https://ai.google.dev/gemini-api/docs/pricing",
    pricingSourceCheckedAt: "2026-06-16",
    maxContextTokens: 1_000_000,
    description:
      "~4× faster than frontier models. Optimized for agent workflows, multi-file coding, document analysis. Multimodal input (text, images, audio, video, PDFs).",
    strongestFor:
      "Agent workflows, multi-file coding, document analysis, multimodal tasks",
    releasedAt: "2026-05-19",
    dataCutoff: "2025-01",
    displayName: "Gemini 3.5 Flash",
    version: "3.5 Flash",
    maxContext: "1M",
    maxOutput: "66K",
    latency: 12,
    icon: "view_in_ar",
    featured: true,
    supportedParams: [
      "model",
      "contents",
      "temperature",
      "top_p",
      "top_k",
      "max_output_tokens",
      "candidate_count",
      "stop_sequences",
      "response_mime_type",
      "response_schema",
      "safety_settings",
      "system_instruction",
      "tools",
      "tool_config",
      "cached_content",
      "service_tier",
      "thinking",
    ],
    routing: {
      modelId: "gemini-3.5-flash",
      description:
        "Fast multimodal model — 4× faster than frontier, optimized for agentic workflows and coding",
      strongestFor: [
        "text_generalist",
        "coding",
        "low_latency",
        "image_understanding",
        "document_understanding",
        "tool_use",
      ],
      scores: {
        code: 0.91,
        math: 0.89,
        writing: 0.89,
        analysis: 0.9,
        qa_factual: 0.88,
        translation: 0.86,
        extraction: 0.9,
        classification: 0.88,
        creative: 0.88,
        reasoning: 0.9,
        long_context: 0.94,
        tool_use: 0.93,
        multimodal: 0.95,
        safety: 0.92,
      },
    },
  },
  "gemini-3.6-flash": {
    family: "google",
    inputPricePerMillion: 0.75,
    outputPricePerMillion: 3.75,
    cacheReadPricePerMillion: 0.075,
    cacheCreationPricePerMillion: 1,
    pricingSourceModelId: "google/gemini-3.6-flash",
    pricingSourceUrl: "https://ai.google.dev/gemini-api/docs/pricing",
    pricingSourceCheckedAt: "2026-08-26",
    pricingNotes:
      "Introductory pricing through 2026-12-31; rises to $1.50/$7.50 per million input/output on 2027-01-01. The prior config value had already jumped to the post-2027 rate even though the introductory period hadn't ended — a ~2x overcharge.",
    maxContextTokens: 1_000_000,
    description:
      "Google's next-gen fast multimodal model with upgraded agentic capabilities. Supports text, images, audio, video, and PDFs.",
    strongestFor:
      "Agent workflows, multi-file coding, document analysis, multimodal tasks",
    releasedAt: "2026-07-21",
    dataCutoff: "2025-01",
    displayName: "Gemini 3.6 Flash",
    version: "3.6 Flash",
    maxContext: "1M",
    maxOutput: "66K",
    latency: 12,
    icon: "view_in_ar",
    featured: true,
    supportedParams: [
      "model",
      "contents",
      "temperature",
      "top_p",
      "top_k",
      "max_output_tokens",
      "candidate_count",
      "stop_sequences",
      "response_mime_type",
      "response_schema",
      "safety_settings",
      "system_instruction",
      "tools",
      "tool_config",
      "cached_content",
      "service_tier",
      "thinking",
    ],
    routing: {
      modelId: "gemini-3.6-flash",
      description:
        "Google's next-gen fast multimodal model — upgraded agentic capabilities, optimized for focused sub-tasks",
      strongestFor: [
        "text_generalist",
        "coding",
        "low_latency",
        "image_understanding",
        "document_understanding",
        "tool_use",
      ],
      scores: {
        code: 0.92,
        math: 0.9,
        writing: 0.9,
        analysis: 0.91,
        qa_factual: 0.89,
        translation: 0.87,
        extraction: 0.91,
        classification: 0.89,
        creative: 0.89,
        reasoning: 0.91,
        long_context: 0.94,
        tool_use: 0.94,
        multimodal: 0.95,
        safety: 0.92,
      },
    },
  },
  "gemini-3.7-flash": {
    family: "google",
    inputPricePerMillion: 0.75,
    outputPricePerMillion: 3.75,
    cacheReadPricePerMillion: 0.075,
    cacheCreationPricePerMillion: 1,
    pricingSourceModelId: "google/gemini-3.7-flash",
    pricingSourceUrl: "https://ai.google.dev/gemini-api/docs/pricing",
    pricingSourceCheckedAt: "2026-08-25",
    pricingNotes:
      "Introductory pricing through 2026-12-31; rises to $1.50/$7.50 per million input/output on 2027-01-01. Google bills context caching as a $0.075/M read plus $0.50/M tokens/hour storage fee rather than a discrete per-token write price — cacheCreationPricePerMillion mirrors the Gemini 3.5/3.6 Flash placeholder. OpenRouter lists a promotional $0.375/$1.875 resale rate, but this entry follows Google's own official list price per this family's convention.",
    maxContextTokens: 1_048_576,
    description:
      "Google's next-gen fast multimodal model targeting coding and agentic workflows, with an aggressive introductory price cut. Supports text, images, audio, video, and PDFs.",
    strongestFor:
      "Agent workflows, multi-file coding, document analysis, multimodal tasks",
    releasedAt: "2026-08-13",
    displayName: "Gemini 3.7 Flash",
    version: "3.7 Flash",
    maxContext: "1M",
    maxOutput: "64K",
    latency: 11,
    icon: "view_in_ar",
    featured: true,
    supportedParams: [
      "model",
      "contents",
      "temperature",
      "top_p",
      "top_k",
      "max_output_tokens",
      "candidate_count",
      "stop_sequences",
      "response_mime_type",
      "response_schema",
      "safety_settings",
      "system_instruction",
      "tools",
      "tool_config",
      "cached_content",
      "service_tier",
      "thinking",
    ],
    routing: {
      modelId: "gemini-3.7-flash",
      description:
        "Google's next-gen fast multimodal model — tuned for coding and agent workloads at a steep introductory discount",
      strongestFor: [
        "text_generalist",
        "coding",
        "low_latency",
        "image_understanding",
        "document_understanding",
        "tool_use",
      ],
      scores: {
        code: 0.93,
        math: 0.91,
        writing: 0.9,
        analysis: 0.92,
        qa_factual: 0.9,
        translation: 0.87,
        extraction: 0.92,
        classification: 0.9,
        creative: 0.89,
        reasoning: 0.92,
        long_context: 0.95,
        tool_use: 0.95,
        multimodal: 0.95,
        safety: 0.92,
      },
    },
  },
  "gemini-3.8-flash": {
    family: "google",
    inputPricePerMillion: 1.5,
    outputPricePerMillion: 7.5,
    cacheReadPricePerMillion: 0.15,
    cacheCreationPricePerMillion: 1.5,
    pricingSourceModelId: "google/gemini-3.8-flash",
    pricingSourceUrl: "https://openrouter.ai/google/gemini-3.8-flash",
    pricingSourceCheckedAt: "2026-09-02",
    pricingNotes:
      "Standard non-discounted base list price: $1.50/M input, $7.50/M output, $0.15/M cache read. (50% promotional rates of $0.75/$3.75/$0.075 excluded per standard non-discount pricing policy).",
    maxContextTokens: 1_048_576,
    description:
      "Google's most intelligent Flash model with significant gains over 3.7 Flash across software engineering, agentic tasks, and multi-step reasoning. Multimodal input accepting text, images, video, audio, and documents/PDFs.",
    strongestFor:
      "Software engineering, multi-file agentic tasks, multi-step reasoning, multimodal analysis (image/video/audio/PDF)",
    releasedAt: "2026-09-02",
    displayName: "Gemini 3.8 Flash",
    version: "3.8 Flash",
    maxContext: "1M",
    maxOutput: "66K",
    latency: 10,
    icon: "view_in_ar",
    featured: true,
    supportedParams: [
      "model",
      "contents",
      "temperature",
      "top_p",
      "top_k",
      "max_output_tokens",
      "candidate_count",
      "stop_sequences",
      "response_mime_type",
      "response_schema",
      "safety_settings",
      "system_instruction",
      "tools",
      "tool_config",
      "cached_content",
      "service_tier",
      "thinking",
    ],
    routing: {
      modelId: "gemini-3.8-flash",
      description:
        "Google's upgraded Flash model — major gains in software engineering, multi-step reasoning, and agentic workflows with 1M context",
      strongestFor: [
        "text_generalist",
        "coding",
        "reasoning",
        "low_latency",
        "image_understanding",
        "document_understanding",
        "tool_use",
      ],
      scores: {
        code: 0.94,
        math: 0.92,
        writing: 0.91,
        analysis: 0.93,
        qa_factual: 0.91,
        translation: 0.88,
        extraction: 0.93,
        classification: 0.91,
        creative: 0.9,
        reasoning: 0.93,
        long_context: 0.96,
        tool_use: 0.96,
        multimodal: 0.96,
        safety: 0.93,
      },
    },
  },
  "gemini-4-argon": {
    family: "google",
    inputPricePerMillion: 4,
    outputPricePerMillion: 20,
    cacheReadPricePerMillion: 0.2,
    cacheCreationPricePerMillion: 0.375,
    pricingSourceModelId: "google/gemini-4-argon",
    pricingSourceUrl: "https://openrouter.ai/google/gemini-4-argon",
    pricingSourceCheckedAt: "2026-10-21",
    pricingNotes:
      "Standard non-discounted list price: $4/M input, $20/M output, $0.20/M cache read, $0.375/M cache write. Introductory rates of $2/$10/$0.10/$0.1875 (what OpenRouter currently shows) end 2026-11-30 and are excluded per standard non-discount pricing policy. Thinking tokens bill as output.",
    maxContextTokens: 1_048_576,
    description:
      "Google's flagship reasoning model for complex software engineering, deep mathematical and scientific problem solving, and autonomous multi-step agents. Multimodal input accepting text, images, video, audio, and documents/PDFs.",
    strongestFor:
      "Complex software engineering, math and science reasoning, autonomous multi-step agents, multimodal analysis",
    releasedAt: "2026-10-13",
    displayName: "Gemini 4 Argon",
    version: "4 Argon",
    maxContext: "1M",
    maxOutput: "943K",
    icon: "view_in_ar",
    supportedParams: [
      "model",
      "contents",
      "temperature",
      "top_p",
      "top_k",
      "max_output_tokens",
      "candidate_count",
      "stop_sequences",
      "response_mime_type",
      "response_schema",
      "safety_settings",
      "system_instruction",
      "tools",
      "tool_config",
      "cached_content",
      "service_tier",
      "thinking",
    ],
  },
  "gemini-3.5-flash-lite": {
    family: "google",
    inputPricePerMillion: 0.3,
    outputPricePerMillion: 2.5,
    cacheReadPricePerMillion: 0.03,
    cacheCreationPricePerMillion: 0.2,
    pricingSourceModelId: "google/gemini-3.5-flash-lite",
    pricingSourceUrl: "https://ai.google.dev/gemini-api/docs/pricing",
    pricingSourceCheckedAt: "2026-07-30",
    maxContextTokens: 1_000_000,
    description:
      "Google's high-efficiency multimodal model optimized for low-latency, high-volume workloads.",
    strongestFor:
      "High-volume tasks, lightweight agentic workflows, cost-sensitive multimodal applications",
    releasedAt: "2026-07",
    dataCutoff: "2025-01",
    displayName: "Gemini 3.5 Flash Lite",
    version: "3.5 Flash Lite",
    maxContext: "1M",
    maxOutput: "66K",
    latency: 8,
    icon: "view_in_ar",
    supportedParams: [
      "model",
      "contents",
      "temperature",
      "top_p",
      "top_k",
      "max_output_tokens",
      "candidate_count",
      "stop_sequences",
      "response_mime_type",
      "response_schema",
      "safety_settings",
      "system_instruction",
      "tools",
      "tool_config",
      "cached_content",
      "service_tier",
    ],
  },

  // ========================================
  // DEEPSEEK MODELS
  // Verified against OpenRouter model pricing (checked 2026-05-22).
  // ========================================
  "deepseek-v3.2": {
    family: "deepseek",
    inputPricePerMillion: 0.26,
    outputPricePerMillion: 0.378,
    cacheReadPricePerMillion: 0.13,
    cacheCreationPricePerMillion: 0.26,
    pricingSourceModelId: "deepseek/deepseek-v3.2",
    pricingSourceUrl: "https://openrouter.ai/deepseek/deepseek-v3.2",
    pricingSourceCheckedAt: "2026-08-26",
    pricingNotes:
      "Modal rate across OpenRouter's 14 providers (set by AtlasCloud + DeepInfra). Cache-read was stale at 5x too low. OpenRouter does not publish a separate input_cache_write price; cache creation is priced as normal input.",
    maxContextTokens: 163_840,
    description:
      "Reasoning-first model built for agents. Competes with GPT-5 level performance.",
    strongestFor:
      "Reasoning, coding, math, agentic workflows, cost-effective intelligence",
    releasedAt: "2025-12",
    displayName: "DeepSeek V3.2",
    version: "V3.2",
    maxContext: "160K",
    maxOutput: "66K",
    latency: 14,
    icon: "neurology",
    featured: true,
    supportedParams: [
      "model",
      "messages",
      "temperature",
      "top_p",
      "max_tokens",
      "stream",
      "stream_options",
      "stop",
      "response_format",
      "tools",
      "tool_choice",
      "thinking",
      "logprobs",
      "top_logprobs",
      "user_id",
    ],
    routing: {
      modelId: "deepseek-v3.2",
      description:
        "Reasoning-first open(-ish) model — strong math and agents, competitive with GPT-5 tier at lower cost",
      strongestFor: ["open_weight", "coding", "reasoning", "math", "low_cost"],
      scores: {
        code: 0.89,
        math: 0.9,
        writing: 0.86,
        analysis: 0.88,
        qa_factual: 0.87,
        translation: 0.84,
        extraction: 0.88,
        classification: 0.86,
        creative: 0.85,
        reasoning: 0.9,
        long_context: 0.92,
        tool_use: 0.91,
        multimodal: 0.88,
        safety: 0.88,
      },
    },
  },
  "deepseek-v4-pro-0813": {
    family: "deepseek",
    inputPricePerMillion: 1.32,
    outputPricePerMillion: 3.96,
    cacheReadPricePerMillion: 0.044,
    cacheCreationPricePerMillion: 1.32,
    pricingSourceModelId: "deepseek/deepseek-v4-pro-0813",
    pricingSourceUrl: "https://openrouter.ai/deepseek/deepseek-v4-pro-0813",
    pricingSourceCheckedAt: "2026-08-28",
    pricingNotes:
      "Modal rate across OpenRouter's 14 providers: $1.32/$3.96/$0.044 per million input/output/cache-read tokens. The DeepSeek provider's $0.66/$1.98/$0.022 off-peak rate is time-dependent and is not used as the static catalog price. OpenRouter does not publish a separate input_cache_write price; cache creation is priced as normal input.",
    maxContextTokens: 1_048_576,
    description:
      "GA release of DeepSeek V4 Pro with enhanced agentic coding, tool use, and long-horizon reasoning.",
    strongestFor:
      "Agentic coding, complex reasoning, tool use, long-horizon workflows",
    releasedAt: "2026-08-13",
    displayName: "DeepSeek V4 Pro 0813",
    version: "V4 Pro 0813",
    maxContext: "1M",
    maxOutput: "384K",
    supportedParams: [
      "model",
      "messages",
      "temperature",
      "top_p",
      "max_tokens",
      "stream",
      "stream_options",
      "stop",
      "response_format",
      "tools",
      "tool_choice",
      "thinking",
      "logprobs",
      "top_logprobs",
      "user_id",
    ],
  },
  "deepseek-v4-flash-0731": {
    family: "deepseek",
    inputPricePerMillion: 0.14,
    outputPricePerMillion: 0.28,
    cacheReadPricePerMillion: 0.028,
    cacheCreationPricePerMillion: 0.14,
    pricingSourceModelId: "deepseek/deepseek-v4-flash-0731",
    pricingSourceUrl: "https://openrouter.ai/deepseek/deepseek-v4-flash-0731",
    pricingSourceCheckedAt: "2026-08-28",
    pricingNotes:
      "Modal rate across OpenRouter providers: $0.14/$0.28/$0.028 per million input/output/cache-read tokens. The DeepSeek provider's peak/off-peak rates are time-dependent and are not used as the static catalog price. OpenRouter does not publish a separate input_cache_write price; cache creation is priced as normal input.",
    maxContextTokens: 1_048_576,
    description:
      "Re-post-trained GA release of DeepSeek V4 Flash optimized for fast, economical coding, reasoning, and agent workflows.",
    strongestFor:
      "Fast agentic coding, tool use, reasoning, high-volume workloads",
    releasedAt: "2026-07-31",
    displayName: "DeepSeek V4 Flash 0731",
    version: "V4 Flash 0731",
    maxContext: "1M",
    maxOutput: "384K",
    supportedParams: [
      "model",
      "messages",
      "temperature",
      "top_p",
      "max_tokens",
      "stream",
      "stream_options",
      "stop",
      "response_format",
      "tools",
      "tool_choice",
      "thinking",
      "logprobs",
      "top_logprobs",
      "user_id",
    ],
  },
  "deepseek-v4.1-flash-1017": {
    family: "deepseek",
    inputPricePerMillion: 0.15,
    outputPricePerMillion: 0.6,
    cacheReadPricePerMillion: 0.003,
    cacheCreationPricePerMillion: 0.15,
    pricingSourceModelId: "deepseek/deepseek-v4.1-flash-1017",
    pricingSourceUrl: "https://openrouter.ai/deepseek/deepseek-v4.1-flash-1017",
    pricingSourceCheckedAt: "2026-10-21",
    pricingNotes:
      "Off-peak rate (weekends and most weekday hours): $0.15/$0.60/$0.003 per million input/output/cache-read tokens. Weekday peak windows (01:00-04:00 and 06:00-10:00 UTC) double it to $0.30/$1.20/$0.006; the time-dependent peak rate is not used as the static catalog price. OpenRouter does not publish a separate input_cache_write price; cache creation is priced as normal input.",
    maxContextTokens: 1_048_576,
    description:
      "October 17, 2026 snapshot of DeepSeek V4.1 Flash, a sparse mixture-of-experts model for fast, economical coding, reasoning, and agent workflows. Accepts text and image input.",
    strongestFor:
      "Fast agentic coding, tool use, reasoning, high-volume workloads",
    releasedAt: "2026-10-17",
    displayName: "DeepSeek V4.1 Flash 1017",
    version: "V4.1 Flash 1017",
    maxContext: "1M",
    maxOutput: "384K",
    supportedParams: [
      "model",
      "messages",
      "temperature",
      "top_p",
      "max_tokens",
      "stream",
      "stream_options",
      "stop",
      "response_format",
      "tools",
      "tool_choice",
      "thinking",
      "logprobs",
      "top_logprobs",
      "user_id",
    ],
  },
  "deepseek-v4-pro": {
    family: "deepseek",
    inputPricePerMillion: 1.74,
    outputPricePerMillion: 3.48,
    cacheReadPricePerMillion: 0.145,
    cacheCreationPricePerMillion: 1.74,
    pricingSourceModelId: "deepseek/deepseek-v4-pro",
    pricingSourceUrl: "https://openrouter.ai/deepseek/deepseek-v4-pro",
    pricingSourceCheckedAt: "2026-08-26",
    pricingNotes:
      "OpenRouter aggregates 17 providers ranging $0.79-$1.91/M input; $1.74/$3.48/$0.145 (in/out/cache-read) is the modal rate shared by the most providers (Together, Baseten, Fireworks, Parasail). OpenRouter does not publish a separate input_cache_write price; cache creation is priced as normal input.",
    maxContextTokens: 1_048_576,
    description:
      "Reasoning-first model built for agents. Competes with GPT-5 level performance.",
    strongestFor:
      "Reasoning, coding, math, agentic workflows, cost-effective intelligence",
    releasedAt: "2026-04-24",
    displayName: "DeepSeek V4 Pro 0423",
    version: "V4 Pro 0423",
    maxContext: "1.05M",
    maxOutput: "384K",
    supportedParams: [
      "model",
      "messages",
      "temperature",
      "top_p",
      "max_tokens",
      "stream",
      "stream_options",
      "stop",
      "response_format",
      "tools",
      "tool_choice",
      "thinking",
      "logprobs",
      "top_logprobs",
      "user_id",
    ],
  },
  "deepseek-v4-flash": {
    family: "deepseek",
    inputPricePerMillion: 0.14,
    outputPricePerMillion: 0.28,
    cacheReadPricePerMillion: 0.028,
    cacheCreationPricePerMillion: 0.14,
    pricingSourceModelId: "deepseek/deepseek-v4-flash",
    pricingSourceUrl: "https://openrouter.ai/deepseek/deepseek-v4-flash",
    pricingSourceCheckedAt: "2026-08-25",
    pricingNotes:
      "OpenRouter aggregates 17 providers ranging $0.068-$0.44/M input; $0.14/$0.28/$0.028 (in/out/cache-read) is the modal rate shared by the most providers (Novita, AtlasCloud, Baidu Qianfan, CoreWeave, Parasail). OpenRouter does not publish a separate input_cache_write price; cache creation is priced as normal input.",
    maxContextTokens: 1_048_576,
    description:
      "Reasoning-first model built for agents. Competes with GPT-5 level performance.",
    strongestFor:
      "Reasoning, coding, math, agentic workflows, cost-effective intelligence",
    releasedAt: "2026-04-24",
    displayName: "DeepSeek V4 Flash 0423",
    version: "V4 Flash 0423",
    maxContext: "1.05M",
    maxOutput: "384K",
    supportedParams: [
      "model",
      "messages",
      "temperature",
      "top_p",
      "max_tokens",
      "stream",
      "stream_options",
      "stop",
      "response_format",
      "tools",
      "tool_choice",
      "thinking",
      "logprobs",
      "top_logprobs",
      "user_id",
    ],
  },
  "deepseek-v4-flash-vision-exp": {
    family: "deepseek",
    inputPricePerMillion: 0.22,
    outputPricePerMillion: 0.66,
    cacheReadPricePerMillion: 0.007,
    cacheCreationPricePerMillion: 0.22,
    pricingSourceModelId: "deepseek/deepseek-v4-flash-vision-exp",
    pricingSourceUrl:
      "https://openrouter.ai/deepseek/deepseek-v4-flash-vision-exp",
    pricingSourceCheckedAt: "2026-09-02",
    pricingNotes:
      "Starting provider rate via Fireworks: $0.22/$0.66/$0.007 per million input/output/cache-read tokens (DeepSeek/Novita list at $0.44/$1.32). OpenRouter does not publish a separate input_cache_write price; cache creation is priced as normal input.",
    maxContextTokens: 1_048_576,
    description:
      "Experimental vision-enabled version of DeepSeek V4 Flash 0731. Sparse mixture-of-experts model (13B active of 284B total) accepting text and image inputs for visual question answering, document/chart understanding, and multimodal agent workflows.",
    strongestFor:
      "Document and chart understanding, visual question answering, multimodal agent workflows, cost-effective vision reasoning",
    releasedAt: "2026-08-21",
    displayName: "DeepSeek V4 Flash Vision Exp",
    version: "V4 Flash Vision Exp",
    maxContext: "1M",
    maxOutput: "384K",
    supportedParams: [
      "model",
      "messages",
      "temperature",
      "top_p",
      "max_tokens",
      "stream",
      "stream_options",
      "stop",
      "response_format",
      "tools",
      "tool_choice",
      "thinking",
      "logprobs",
      "top_logprobs",
      "user_id",
    ],
  },

  // ========================================
  // MINIMAX MODELS
  // Verified against OpenRouter model pricing (checked 2026-05-22).
  "minimax-m2.7": {
    family: "minimax",
    inputPricePerMillion: 0.3,
    outputPricePerMillion: 1.2,
    cacheReadPricePerMillion: 0.06,
    cacheCreationPricePerMillion: 0.3,
    pricingSourceModelId: "minimax/minimax-m2.7",
    pricingSourceUrl: "https://openrouter.ai/minimax/minimax-m2.7",
    pricingSourceCheckedAt: "2026-08-26",
    pricingNotes:
      "Modal rate across providers (AtlasCloud, MiniMax, Fireworks, GMICloud). Cache-read was incorrectly set equal to input price; the real cache-read discount is ~20% of input.",
    maxContextTokens: 204_800,
    description:
      "Next-generation model for autonomous real-world productivity with multi-agent collaboration. Excels at live debugging and document generation.",
    strongestFor:
      "Agentic workflows, live debugging, financial modeling, document generation",
    releasedAt: "2026-03",
    displayName: "MiniMax M2.7",
    version: "M2.7",
    maxContext: "205K",
    maxOutput: "131K",
    latency: 14,
    icon: "hub",
    supportedParams: [
      "model",
      "messages",
      "max_tokens",
      "temperature",
      "top_p",
      "stream",
      "stop",
      "tools",
      "tool_choice",
      "response_format",
    ],
  },
  // ========================================
  "minimax-m2.5": {
    family: "minimax",
    inputPricePerMillion: 0.3,
    outputPricePerMillion: 1.2,
    cacheReadPricePerMillion: 0.03,
    cacheCreationPricePerMillion: 0.3,
    pricingSourceModelId: "minimax/minimax-m2.5",
    pricingSourceUrl: "https://openrouter.ai/minimax/minimax-m2.5",
    pricingSourceCheckedAt: "2026-08-26",
    pricingNotes:
      "Modal rate across providers (NovitaAI, MiniMax, SiliconFlow, and others). Cache-read was incorrectly set equal to input price; the real cache-read discount is ~10% of input.",
    maxContextTokens: 204_800,
    description:
      "SOTA model for real-world productivity. Strong at coding and office tasks.",
    strongestFor:
      "Coding (SWE-Bench 80.2%), agentic workflows, office productivity, browsing",
    releasedAt: "2026-02",
    displayName: "MiniMax M2.5",
    version: "M2.5",
    maxContext: "205K",
    maxOutput: "197K",
    supportedParams: [
      "model",
      "messages",
      "max_tokens",
      "temperature",
      "top_p",
      "stream",
      "stop",
      "tools",
      "tool_choice",
      "response_format",
    ],
  },
  "minimax-m2.1": {
    family: "minimax",
    inputPricePerMillion: 0.3,
    outputPricePerMillion: 1.2,
    cacheReadPricePerMillion: 0.03,
    cacheCreationPricePerMillion: 0.3,
    pricingSourceModelId: "minimax/minimax-m2.1",
    pricingSourceUrl: "https://openrouter.ai/minimax/minimax-m2.1",
    pricingSourceCheckedAt: "2026-08-26",
    pricingNotes:
      "OpenRouter does not publish a separate input_cache_write price; cache creation is priced as normal input.",
    maxContextTokens: 204_800,
    description: "Optimized for coding and agentic workflows.",
    strongestFor: "Coding, agentic workflows, application development",
    releasedAt: "2025-12",
    displayName: "MiniMax M2.1",
    version: "M2.1",
    maxContext: "205K",
    maxOutput: "197K",
    supportedParams: [
      "model",
      "messages",
      "max_tokens",
      "temperature",
      "top_p",
      "stream",
      "stop",
      "tools",
      "tool_choice",
      "response_format",
    ],
  },
  "minimax-m3": {
    family: "minimax",
    inputPricePerMillion: 0.3,
    outputPricePerMillion: 1.2,
    cacheReadPricePerMillion: 0.06,
    cacheCreationPricePerMillion: 0.3,
    pricingSourceModelId: "minimax/minimax-m3",
    pricingSourceUrl: "https://openrouter.ai/minimax/minimax-m3",
    pricingSourceCheckedAt: "2026-08-26",
    pricingNotes:
      "Launch promotion: 50% discount ($0.06 input / $0.60 output). Base pricing shown here. Cache-read corrected from a stale $0.12 (GMICloud's outlier) to the modal $0.06 shared by 7 providers.",
    maxContextTokens: 1_000_000,
    description:
      "Sparse MoE with MiniMax Sparse Attention (MSA). 15.6x faster decoding at 1M context. Multimodal (text, image, video).",
    strongestFor:
      "Frontier coding, long-horizon agentic tasks, multimodal analysis",
    releasedAt: "2026-05-31",
    displayName: "MiniMax M3",
    version: "M3",
    maxContext: "1M",
    maxOutput: "131K",
    latency: 14,
    icon: "hub",
    featured: true,
    supportedParams: [
      "model",
      "messages",
      "max_tokens",
      "temperature",
      "top_p",
      "stream",
      "stop",
      "tools",
      "tool_choice",
      "response_format",
    ],
    routing: {
      modelId: "minimax-m3",
      description:
        "Frontier sparse MoE with 1M context — 15.6× faster decoding, multimodal coding capabilities",
      strongestFor: [
        "coding",
        "long_context",
        "tool_use",
        "image_understanding",
        "low_cost",
      ],
      scores: {
        code: 0.92,
        math: 0.9,
        writing: 0.88,
        analysis: 0.9,
        qa_factual: 0.88,
        translation: 0.85,
        extraction: 0.89,
        classification: 0.87,
        creative: 0.87,
        reasoning: 0.91,
        long_context: 0.96,
        tool_use: 0.93,
        multimodal: 0.94,
        safety: 0.89,
      },
    },
  },
  // 'minimax-m2': {
  //   family: 'minimax',
  //   capabilities: ['chat'],
  //   inputPricePerMillion: 0.3,
  //   outputPricePerMillion: 1.2,
  //   cacheReadPricePerMillion: 0.03,
  //   cacheCreationPricePerMillion: 0.3,
  // },

  // ========================================
  // GLM MODELS (ZAI)
  // Verified against OpenRouter model pricing (checked 2026-05-22).
  // ========================================
  "glm-4.7": {
    family: "zai",
    inputPricePerMillion: 0.6,
    outputPricePerMillion: 2.2,
    cacheReadPricePerMillion: 0.11,
    cacheCreationPricePerMillion: 0.6,
    pricingSourceModelId: "z-ai/glm-4.7",
    pricingSourceUrl: "https://openrouter.ai/z-ai/glm-4.7",
    pricingSourceCheckedAt: "2026-08-26",
    pricingNotes:
      "Modal rate across 8 providers (Google Vertex + Z.ai for input/output; Venice + Z.ai for cache-read), up from a now-stale cheapest-provider snapshot. OpenRouter does not publish a separate input_cache_write price; cache creation is priced as normal input.",
    maxContextTokens: 202_752,
    description: "Zhipu AI GLM model with vision support.",
    strongestFor: "General chat and vision tasks",
    releasedAt: "2025-12",
    displayName: "GLM 4.7",
    version: "4.7",
    maxContext: "203K",
    maxOutput: "131K",
    supportedParams: [
      "model",
      "messages",
      "do_sample",
      "temperature",
      "top_p",
      "max_tokens",
      "stream",
      "thinking",
      "tools",
      "tool_choice",
    ],
  },
  "glm-5": {
    family: "zai",
    inputPricePerMillion: 1,
    outputPricePerMillion: 3.2,
    cacheReadPricePerMillion: 0.2,
    cacheCreationPricePerMillion: 1,
    pricingSourceModelId: "z-ai/glm-5",
    pricingSourceUrl: "https://openrouter.ai/z-ai/glm-5",
    pricingSourceCheckedAt: "2026-08-26",
    pricingNotes:
      "Modal rate across a 5-provider cluster (Venice, Z.ai, NovitaAI, Amazon Bedrock, DigitalOcean), up from a now-stale cheapest-provider snapshot. OpenRouter does not publish a separate input_cache_write price; cache creation is priced as normal input.",
    maxContextTokens: 202_752,
    description: "Zhipu AI GLM model with vision support.",
    strongestFor: "General chat and vision tasks",
    releasedAt: "2026-02",
    displayName: "GLM 5",
    version: "5",
    maxContext: "203K",
    maxOutput: "Unspecified",
    supportedParams: [
      "model",
      "messages",
      "do_sample",
      "temperature",
      "top_p",
      "max_tokens",
      "stream",
      "thinking",
      "tools",
      "tool_choice",
    ],
  },
  "glm-5.1": {
    family: "zai",
    inputPricePerMillion: 1.4,
    outputPricePerMillion: 4.4,
    cacheReadPricePerMillion: 0.26,
    cacheCreationPricePerMillion: 1.4,
    pricingSourceModelId: "z-ai/glm-5.1",
    pricingSourceUrl: "https://openrouter.ai/z-ai/glm-5.1",
    pricingSourceCheckedAt: "2026-08-26",
    pricingNotes:
      "Cache-read now confirmed via OpenRouter's 17-provider listing (modal $0.26, set by 6 providers), replacing the earlier 20%-ratio estimate. Context window confirmed still 203K — has not received the 1M bump that GLM 5.2/5.3 have.",
    maxContextTokens: 202_752,
    description:
      "Autonomous reasoning model with 8+ hour planning capability. Specialized for long-horizon tasks, autonomous planning, and complex engineering.",
    strongestFor:
      "Long-horizon tasks, autonomous planning, complex engineering, agentic workflows",
    releasedAt: "2025-12",
    displayName: "GLM 5.1",
    version: "5.1",
    maxContext: "203K",
    maxOutput: "64K",
    latency: 18,
    icon: "psychology",
    supportedParams: [
      "model",
      "messages",
      "do_sample",
      "temperature",
      "top_p",
      "max_tokens",
      "stream",
      "thinking",
      "tools",
      "tool_choice",
    ],
    routing: {
      modelId: "glm-5.1",
      description:
        "Autonomous reasoning model — 8+ hour planning horizon for complex engineering and long-horizon tasks",
      strongestFor: ["reasoning", "tool_use", "coding", "text_generalist"],
      scores: {
        code: 0.87,
        math: 0.86,
        writing: 0.84,
        analysis: 0.87,
        qa_factual: 0.85,
        translation: 0.82,
        extraction: 0.86,
        classification: 0.84,
        creative: 0.83,
        reasoning: 0.91,
        long_context: 0.89,
        tool_use: 0.92,
        multimodal: 0.88,
        safety: 0.88,
      },
    },
  },
  "glm-5.2": {
    family: "zai",
    inputPricePerMillion: 1.4,
    outputPricePerMillion: 4.4,
    cacheReadPricePerMillion: 0.26,
    cacheCreationPricePerMillion: 1.4,
    pricingSourceModelId: "z-ai/glm-5.2",
    pricingSourceUrl: "https://openrouter.ai/z-ai/glm-5.2",
    pricingSourceCheckedAt: "2026-06-17",
    pricingNotes:
      "Cache read pricing from OpenRouter (20% typical ratio). 753B-parameter MoE architecture.",
    maxContextTokens: 1_000_000,
    description:
      "Frontier coding-first model with 1M context. 753B-parameter MoE with dual reasoning modes. MIT-licensed weights available.",
    strongestFor:
      "Long-horizon coding, repo-scale agent work, complex engineering, agentic workflows with extended context",
    releasedAt: "2026-06-13",
    displayName: "GLM 5.2",
    version: "5.2",
    maxContext: "1M",
    maxOutput: "131K",
    latency: 18,
    icon: "psychology",
    featured: true,
    supportedParams: [
      "model",
      "messages",
      "do_sample",
      "temperature",
      "top_p",
      "max_tokens",
      "stream",
      "thinking",
      "tools",
      "tool_choice",
    ],
    routing: {
      modelId: "glm-5.2",
      description:
        "Frontier coding model with 1M context — 753B MoE, dual reasoning modes, optimized for long-horizon agentic coding",
      strongestFor: [
        "coding",
        "reasoning",
        "tool_use",
        "long_context",
        "text_generalist",
      ],
      scores: {
        code: 0.91,
        math: 0.89,
        writing: 0.86,
        analysis: 0.89,
        qa_factual: 0.87,
        translation: 0.84,
        extraction: 0.88,
        classification: 0.86,
        creative: 0.85,
        reasoning: 0.92,
        long_context: 0.96,
        tool_use: 0.94,
        multimodal: 0.9,
        safety: 0.89,
      },
    },
  },
  "glm-5.3": {
    family: "zai",
    inputPricePerMillion: 1.4,
    outputPricePerMillion: 4.4,
    cacheReadPricePerMillion: 0.26,
    cacheCreationPricePerMillion: 1.4,
    pricingSourceModelId: "z-ai/glm-5.3",
    pricingSourceUrl: "https://openrouter.ai/z-ai/glm-5.3",
    pricingSourceCheckedAt: "2026-08-25",
    pricingNotes:
      "Same list price as GLM 5.2, confirmed via OpenRouter listing and independent pricing roundups. Reasoning is always-on; effort levels low/high/max are supported (max is default).",
    maxContextTokens: 1_048_576,
    description:
      "Frontier coding-first model improving on GLM 5.2's coding ability and token efficiency at the same 1M-context price point. Always-on reasoning with selectable effort levels.",
    strongestFor:
      "Long-horizon coding, repo-scale agent work, complex engineering, agentic workflows with extended context",
    releasedAt: "2026-08-18",
    displayName: "GLM 5.3",
    version: "5.3",
    maxContext: "1M",
    maxOutput: "131K",
    latency: 17,
    icon: "psychology",
    featured: true,
    supportedParams: [
      "model",
      "messages",
      "do_sample",
      "temperature",
      "top_p",
      "max_tokens",
      "stream",
      "thinking",
      "tools",
      "tool_choice",
    ],
    routing: {
      modelId: "glm-5.3",
      description:
        "Frontier coding model with 1M context — improves on GLM 5.2's coding and token efficiency, always-on reasoning with selectable effort",
      strongestFor: [
        "coding",
        "reasoning",
        "tool_use",
        "long_context",
        "text_generalist",
      ],
      scores: {
        code: 0.92,
        math: 0.9,
        writing: 0.86,
        analysis: 0.9,
        qa_factual: 0.87,
        translation: 0.84,
        extraction: 0.88,
        classification: 0.86,
        creative: 0.85,
        reasoning: 0.93,
        long_context: 0.96,
        tool_use: 0.94,
        multimodal: 0.9,
        safety: 0.89,
      },
    },
  },
  "glm-5.3-flash": {
    family: "zai",
    inputPricePerMillion: 0.15,
    outputPricePerMillion: 0.5,
    cacheReadPricePerMillion: 0.03,
    cacheCreationPricePerMillion: 0.15,
    pricingSourceModelId: "z-ai/glm-5.3-flash",
    pricingSourceUrl: "https://openrouter.ai/z-ai/glm-5.3-flash",
    pricingSourceCheckedAt: "2026-08-27",
    pricingNotes:
      "Modal rate held by 7 of 11 providers (Parasail, Reka AI, Together, Cloudflare, DeepInfra, io.net, Baseten). Z.ai, NovitaAI, and GMICloud currently list a temporary 50% promotional discount ($0.075/$0.25/$0.015) through 2026-09-09 16:00 UTC, not used here since it is time-limited. OpenRouter does not publish a separate input_cache_write price; cache creation is priced as normal input.",
    maxContextTokens: 1_310_720,
    description:
      "Fast, cost-optimized tier of the GLM 5.3 family. Native multimodal (text, image, video input) with a hybrid sparse/linear attention architecture for efficient coding and long-horizon agent tasks.",
    strongestFor:
      "High-volume workloads, fast responses, cost-sensitive coding and agentic tasks",
    releasedAt: "2026-08-26",
    displayName: "GLM 5.3 Flash",
    version: "5.3 Flash",
    maxContext: "1.25M",
    maxOutput: "131K",
    supportedParams: [
      "model",
      "messages",
      "do_sample",
      "temperature",
      "top_p",
      "max_tokens",
      "stream",
      "thinking",
      "tools",
      "tool_choice",
    ],
  },

  // ========================================
  // QWEN MODELS (Alibaba DashScope)
  // Verified against OpenRouter model pricing (checked 2026-05-22).
  // ========================================
  // 'qwen3.5': {
  //   family: 'qwen',
  //   capabilities: ['chat', 'reasoning', 'coding', 'vision'],
  //   inputPricePerMillion: 0.11,
  //   outputPricePerMillion: 0.44,
  //   cacheReadPricePerMillion: 0.011,
  //   cacheCreationPricePerMillion: 0.11,
  // },
  "qwen3-coder-next": {
    family: "qwen",
    inputPricePerMillion: 0.11,
    outputPricePerMillion: 1.5,
    cacheReadPricePerMillion: 0.07,
    cacheCreationPricePerMillion: 0.11,
    pricingSourceModelId: "qwen/qwen3-coder-next",
    pricingSourceUrl: "https://openrouter.ai/qwen/qwen3-coder-next",
    pricingSourceCheckedAt: "2026-08-26",
    pricingNotes:
      "Output corrected to the $1.50 modal price (Novita + Alibaba), up from $0.80 which only matched Parasail, the single cheapest of 4 providers. No provider shares an identical input price, so input is left at the closest ($0.11 vs Parasail's $0.12); cache-read ($0.07) also only matches Parasail — the modal providers don't publish a cache-read rate. OpenRouter does not publish a separate input_cache_write price; cache creation is priced as normal input.",
    maxContextTokens: 262_144,
    description:
      "MoE model specialized for coding agents and local development. Extremely efficient.",
    strongestFor:
      "Coding agents, agentic workflows, tool use, failure recovery",
    releasedAt: "2026-02",
    displayName: "Qwen3 Coder Next",
    version: "Coder Next",
    maxContext: "262K",
    maxOutput: "262K",
    supportedParams: [
      "model",
      "messages",
      "temperature",
      "top_p",
      "max_tokens",
      "frequency_penalty",
      "top_k",
      "repetition_penalty",
      "stream",
      "stop",
    ],
  },
  "qwen3-coder-flash": {
    family: "qwen",
    inputPricePerMillion: 0.195,
    outputPricePerMillion: 0.975,
    cacheReadPricePerMillion: 0.039,
    cacheCreationPricePerMillion: 0.24375,
    pricingSourceModelId: "qwen/qwen3-coder-flash",
    pricingSourceUrl: "https://openrouter.ai/qwen/qwen3-coder-flash",
    pricingSourceCheckedAt: "2026-05-22",
    maxContextTokens: 1_000_000,
    description:
      "Sparse MoE coding model with 30.5B total params, 3.3B active. Extended 1M context for repository-scale operations.",
    strongestFor:
      "Multi-file codebases, agentic coding workflows, complex refactoring, repository-wide analysis",
    releasedAt: "2025-09",
    dataCutoff: "2025-06",
    displayName: "Qwen3 Coder Flash",
    version: "Coder Flash",
    maxContext: "1M",
    maxOutput: "66K",
    supportedParams: [
      "model",
      "messages",
      "temperature",
      "top_p",
      "max_tokens",
      "frequency_penalty",
      "top_k",
      "repetition_penalty",
      "stream",
      "stop",
    ],
  },
  "qwen3.5-flash": {
    family: "qwen",
    inputPricePerMillion: 0.065,
    outputPricePerMillion: 0.26,
    cacheReadPricePerMillion: 0.065,
    cacheCreationPricePerMillion: 0.08125,
    pricingSourceModelId: "qwen/qwen3.5-flash-02-23",
    pricingSourceUrl: "https://openrouter.ai/qwen/qwen3.5-flash-02-23",
    pricingSourceCheckedAt: "2026-05-22",
    pricingNotes:
      "OpenRouter does not publish an input_cache_read price; cache reads are priced as normal input.",
    maxContextTokens: 1_000_000,
    description:
      "Qwen 3.5 Flash - ultra-fast general purpose model with 128K context.",
    strongestFor: "Quick responses, routing, classification, simple queries",
    releasedAt: "2026-02",
    displayName: "Qwen3.5-Flash",
    version: "3.5 Flash",
    maxContext: "1M",
    maxOutput: "66K",
    supportedParams: [
      "model",
      "messages",
      "temperature",
      "top_p",
      "max_tokens",
      "frequency_penalty",
      "top_k",
      "repetition_penalty",
      "stream",
      "stop",
    ],
  },
  "qwen3.6-flash": {
    family: "qwen",
    inputPricePerMillion: 0.1875,
    outputPricePerMillion: 1.125,
    cacheReadPricePerMillion: 0.01875,
    cacheCreationPricePerMillion: 0.1875,
    pricingSourceModelId: "qwen/qwen3.6-flash",
    pricingSourceUrl: "https://openrouter.ai/qwen/qwen3.6-flash",
    pricingSourceCheckedAt: "2026-08-26",
    pricingNotes:
      "OpenRouter pricing with 25% discount applied. Tiered pricing above 256K tokens. Re-verified 2026-08-26: input/output still match; OpenRouter's live API exposes a cache_write price ($0.234375) but no input_cache_read field for this endpoint, so the $0.01875 cache-read figure here is unconfirmed — left unchanged pending a clearer source rather than guessing a replacement.",
    maxContextTokens: 1_000_000,
    description:
      "Fast, efficient model from Alibaba's Qwen 3.6 series. Supports text, image, and video input with 1M context.",
    strongestFor:
      "Multimodal analysis, fast inference, cost-effective processing",
    releasedAt: "2026-04",
    displayName: "Qwen 3.6 Flash",
    version: "3.6 Flash",
    maxContext: "1M",
    maxOutput: "66K",
    latency: 8,
    supportedParams: [
      "model",
      "messages",
      "temperature",
      "top_p",
      "max_tokens",
      "frequency_penalty",
      "top_k",
      "repetition_penalty",
      "stream",
      "stop",
    ],
    routing: {
      modelId: "qwen3.6-flash",
      description:
        "Fast multimodal Qwen 3.6 model — text, image, video input with 1M context at low cost",
      strongestFor: [
        "low_cost",
        "low_latency",
        "text_generalist",
        "image_understanding",
      ],
      scores: {
        code: 0.83,
        math: 0.81,
        writing: 0.82,
        analysis: 0.82,
        qa_factual: 0.83,
        translation: 0.8,
        extraction: 0.83,
        classification: 0.82,
        creative: 0.8,
        reasoning: 0.82,
        long_context: 0.88,
        tool_use: 0.85,
        multimodal: 0.89,
        safety: 0.87,
      },
    },
  },
  "qwen3.7-flash": {
    family: "qwen",
    inputPricePerMillion: 0.03,
    outputPricePerMillion: 0.13,
    cacheReadPricePerMillion: 0.006,
    cacheCreationPricePerMillion: 0.0375,
    pricingSourceModelId: "qwen/qwen3.7-flash",
    pricingSourceUrl: "https://openrouter.ai/qwen/qwen3.7-flash",
    pricingSourceCheckedAt: "2026-08-26",
    pricingNotes:
      "OpenRouter's live endpoint API does publish an input_cache_read price ($0.006, 80% off input) — corrected from the earlier assumption that no cache-read rate existed. Volume-tier overrides above 32K/256K prompt tokens raise all rates ~3-7x; base tier shown here.",
    maxContextTokens: 1_000_000,
    description:
      "Qwen 3.7 Flash - vision-language model positioned for multimodal applications including visual coding, object recognition, and computer interaction tasks.",
    strongestFor:
      "Quick multimodal responses, visual coding, computer-use tasks, cost-sensitive workloads",
    releasedAt: "2026-07",
    displayName: "Qwen3.7-Flash",
    version: "3.7 Flash",
    maxContext: "1M",
    maxOutput: "66K",
    latency: 8,
    supportedParams: [
      "model",
      "messages",
      "temperature",
      "top_p",
      "max_tokens",
      "frequency_penalty",
      "top_k",
      "repetition_penalty",
      "stream",
      "stop",
    ],
    routing: {
      modelId: "qwen3.7-flash",
      description:
        "Vision-language Qwen 3.7 model — visual coding, object recognition, and computer-use at very low cost",
      strongestFor: [
        "low_cost",
        "low_latency",
        "text_generalist",
        "image_understanding",
      ],
      scores: {
        code: 0.84,
        math: 0.82,
        writing: 0.82,
        analysis: 0.83,
        qa_factual: 0.83,
        translation: 0.8,
        extraction: 0.83,
        classification: 0.82,
        creative: 0.8,
        reasoning: 0.82,
        long_context: 0.88,
        tool_use: 0.86,
        multimodal: 0.9,
        safety: 0.87,
      },
    },
  },
  "qwen3.7-max": {
    family: "qwen",
    inputPricePerMillion: 1.475,
    outputPricePerMillion: 4.425,
    cacheReadPricePerMillion: 0.295,
    cacheCreationPricePerMillion: 1.475,
    pricingSourceModelId: "qwen/qwen3.7-max",
    pricingSourceUrl: "https://openrouter.ai/qwen/qwen3.7-max",
    pricingSourceCheckedAt: "2026-08-26",
    pricingNotes:
      "OpenRouter's live endpoint API publishes input_cache_read at $0.295 (20% of input) — the prior $0.1475 was exactly half that, apparently a 5-minute-cache-tier variant rather than the standard cache_read field.",
    maxContextTokens: 1_000_000,
    description:
      "Flagship model in Alibaba's Qwen3.7 series, optimized for agent-centric workloads including coding, office, and productivity tasks.",
    strongestFor:
      "Agentic workflows, coding, office and productivity tasks, complex reasoning",
    releasedAt: "2026-05",
    displayName: "Qwen3.7-Max",
    version: "3.7 Max",
    maxContext: "1M",
    maxOutput: "66K",
    latency: 14,
    featured: true,
    supportedParams: [
      "model",
      "messages",
      "temperature",
      "top_p",
      "max_tokens",
      "frequency_penalty",
      "top_k",
      "repetition_penalty",
      "stream",
      "stop",
    ],
    routing: {
      modelId: "qwen3.7-max",
      description:
        "Alibaba's flagship Qwen3.7 model — agent-centric workloads, coding, and productivity tasks with 1M context",
      strongestFor: [
        "text_generalist",
        "coding",
        "reasoning",
        "long_context",
        "tool_use",
      ],
      scores: {
        code: 0.92,
        math: 0.91,
        writing: 0.88,
        analysis: 0.9,
        qa_factual: 0.88,
        translation: 0.85,
        extraction: 0.89,
        classification: 0.88,
        creative: 0.86,
        reasoning: 0.91,
        long_context: 0.93,
        tool_use: 0.93,
        multimodal: 0.87,
        safety: 0.89,
      },
    },
  },
  "qwen3.8-flash": {
    family: "qwen",
    inputPricePerMillion: 0.15,
    outputPricePerMillion: 0.47,
    cacheReadPricePerMillion: 0.016,
    cacheCreationPricePerMillion: 0.2,
    pricingSourceModelId: "qwen/qwen3.8-flash",
    pricingSourceUrl: "https://openrouter.ai/qwen/qwen3.8-flash",
    pricingSourceCheckedAt: "2026-09-02",
    pricingNotes:
      "Hosted directly on Alibaba Cloud Int. Supports 5-minute cache creation ($0.20/M) and cache read ($0.016/M).",
    maxContextTokens: 1_000_000,
    description:
      "Multimodal reasoning model from Alibaba designed for coding assistance, agentic workflows, visual understanding, and document/codebase analysis. Accepts text, image, and video inputs.",
    strongestFor:
      "Coding assistance, agentic workflows, visual understanding, document and codebase analysis, long-video processing",
    releasedAt: "2026-08-26",
    displayName: "Qwen 3.8 Flash",
    version: "3.8 Flash",
    maxContext: "1M",
    maxOutput: "131K",
    latency: 8,
    supportedParams: [
      "model",
      "messages",
      "temperature",
      "top_p",
      "max_tokens",
      "stream",
      "tools",
      "tool_choice",
      "response_format",
    ],
    routing: {
      modelId: "qwen3.8-flash",
      description:
        "Fast multimodal Qwen 3.8 model — multimodal reasoning, visual understanding, coding, and agentic workflows with 1M context",
      strongestFor: [
        "low_cost",
        "low_latency",
        "text_generalist",
        "coding",
        "image_understanding",
        "tool_use",
      ],
      scores: {
        code: 0.89,
        math: 0.87,
        writing: 0.85,
        analysis: 0.87,
        qa_factual: 0.86,
        translation: 0.84,
        extraction: 0.87,
        classification: 0.86,
        creative: 0.84,
        reasoning: 0.88,
        long_context: 0.92,
        tool_use: 0.9,
        multimodal: 0.93,
        safety: 0.89,
      },
    },
  },
  "qwen3.8-max": {
    family: "qwen",
    inputPricePerMillion: 2,
    outputPricePerMillion: 6,
    cacheReadPricePerMillion: 0.25,
    cacheCreationPricePerMillion: 2.5,
    pricingSourceModelId: "qwen/qwen3.8-max",
    pricingSourceUrl: "https://openrouter.ai/qwen/qwen3.8-max",
    pricingSourceCheckedAt: "2026-09-02",
    pricingNotes:
      "Flagship multimodal offering and general-availability successor to Qwen3.8 Max Preview. 5-minute cache creation is $2.50/M, cache read is $0.25/M (or $0.17/M on 5m cache read).",
    maxContextTokens: 1_000_000,
    description:
      "Alibaba's flagship multimodal reasoning model, general-availability successor to Qwen3.8 Max Preview. Built for complex reasoning, visual understanding, coding, and agentic workflows across text, images, and video.",
    strongestFor:
      "Complex reasoning, visual understanding, advanced coding, multi-modal agent workflows",
    releasedAt: "2026-08-03",
    displayName: "Qwen 3.8 Max",
    version: "3.8 Max",
    maxContext: "1M",
    maxOutput: "131K",
    latency: 14,
    featured: true,
    supportedParams: [
      "model",
      "messages",
      "temperature",
      "top_p",
      "max_tokens",
      "stream",
      "tools",
      "tool_choice",
      "response_format",
    ],
    routing: {
      modelId: "qwen3.8-max",
      description:
        "Alibaba's flagship Qwen 3.8 model — top-tier multimodal reasoning, advanced coding, visual understanding, and agentic workflows with 1M context",
      strongestFor: [
        "text_generalist",
        "coding",
        "reasoning",
        "long_context",
        "tool_use",
        "image_understanding",
      ],
      scores: {
        code: 0.95,
        math: 0.94,
        writing: 0.9,
        analysis: 0.93,
        qa_factual: 0.91,
        translation: 0.88,
        extraction: 0.92,
        classification: 0.9,
        creative: 0.89,
        reasoning: 0.95,
        long_context: 0.96,
        tool_use: 0.96,
        multimodal: 0.94,
        safety: 0.92,
      },
    },
  },

  // ========================================
  // KIMI MODELS (Moonshot AI)
  // Verified against OpenRouter model pricing (checked 2026-05-22).
  "kimi-k3": {
    family: "moonshot",
    inputPricePerMillion: 3,
    outputPricePerMillion: 15,
    cacheReadPricePerMillion: 0.3,
    cacheCreationPricePerMillion: 3,
    pricingSourceModelId: "moonshotai/kimi-k3",
    pricingSourceUrl: "https://openrouter.ai/moonshotai/kimi-k3",
    pricingSourceCheckedAt: "2026-07-30",
    pricingNotes:
      "Cached input billed at $0.30/M (90% off the $3/M cache-miss rate). OpenRouter does not publish a separate cache-write price; cache creation is priced as normal input.",
    maxContextTokens: 1_000_000,
    description:
      "Moonshot AI's 2.8T parameter open-weight multimodal reasoning model, optimized for complex coding, knowledge work, and agentic workflows.",
    strongestFor:
      "Complex coding, knowledge work, long-horizon agentic workflows, multimodal tasks",
    releasedAt: "2026-07",
    displayName: "Kimi K3",
    version: "K3",
    maxContext: "1M",
    maxOutput: "128K",
    latency: 16,
    icon: "nights_stay",
    featured: true,
    supportedParams: [
      "model",
      "messages",
      "max_tokens",
      "max_completion_tokens",
      "temperature",
      "top_p",
      "thinking",
      "presence_penalty",
      "frequency_penalty",
      "n",
      "tool_choice",
      "stream",
      "stop",
    ],
    routing: {
      modelId: "kimi-k3",
      description:
        "Moonshot AI's 2.8T open-weight reasoning model — strong coding and agentic workflows with 1M context",
      strongestFor: [
        "text_generalist",
        "coding",
        "reasoning",
        "long_context",
        "tool_use",
      ],
      scores: {
        code: 0.94,
        math: 0.92,
        writing: 0.89,
        analysis: 0.91,
        qa_factual: 0.88,
        translation: 0.85,
        extraction: 0.89,
        classification: 0.87,
        creative: 0.87,
        reasoning: 0.93,
        long_context: 0.95,
        tool_use: 0.94,
        multimodal: 0.88,
        safety: 0.88,
      },
    },
  },
  "kimi-k2.6": {
    family: "moonshot",
    inputPricePerMillion: 0.95,
    outputPricePerMillion: 4,
    cacheReadPricePerMillion: 0.16,
    cacheCreationPricePerMillion: 0.95,
    pricingSourceModelId: "moonshotai/kimi-k2.6",
    pricingSourceUrl: "https://openrouter.ai/moonshotai/kimi-k2.6",
    pricingSourceCheckedAt: "2026-08-26",
    pricingNotes:
      "Modal rate across 20 endpoints (6 providers: Baidu, Cloudflare, Moonshot AI, AtlasCloud, Fireworks, BaseTen) — the prior $0.73/$3.49/$0.25 matched none of them and was stale on all three fields. OpenRouter does not publish a separate input_cache_write price; cache creation is priced as normal input.",
    maxContextTokens: 262_144,
    description:
      "Moonshot AI next-gen multimodal model for long-horizon coding, UI/UX generation, and multi-agent orchestration with agent swarm architecture.",
    strongestFor:
      "Long-horizon coding, UI/UX generation, multi-agent orchestration, multimodal tasks",
    releasedAt: "2026-04",
    displayName: "Kimi K2.6",
    version: "K2.6",
    maxContext: "262K",
    maxOutput: "262K",
    latency: 16,
    icon: "nights_stay",
    featured: true,
    supportedParams: [
      "model",
      "messages",
      "max_tokens",
      "max_completion_tokens",
      "temperature",
      "top_p",
      "thinking",
      "presence_penalty",
      "frequency_penalty",
      "n",
      "tool_choice",
      "stream",
      "stop",
    ],
  },
  // ========================================
  "kimi-k2.5": {
    family: "moonshot",
    inputPricePerMillion: 0.45,
    outputPricePerMillion: 2.25,
    cacheReadPricePerMillion: 0.07,
    cacheCreationPricePerMillion: 0.45,
    pricingSourceModelId: "moonshotai/kimi-k2.5",
    pricingSourceUrl: "https://openrouter.ai/moonshotai/kimi-k2.5",
    pricingSourceCheckedAt: "2026-08-26",
    pricingNotes:
      "10 providers found on OpenRouter, each at a distinct price point except DeepInfra and SiliconFlow, which tie at $0.45/$2.25/$0.07 — the largest matching cluster, used here in place of the prior stale $0.4/$1.9/$0.09. OpenRouter does not publish a separate input_cache_write price; cache creation is priced as normal input.",
    maxContextTokens: 262_144,
    description:
      "1T parameter MoE with native multimodal. Agent Swarm for parallel task decomposition.",
    strongestFor:
      "Multimodal coding, vision-based code generation, agentic workflows, search integration",
    releasedAt: "2026-01",
    displayName: "Kimi K2.5",
    version: "K2.5",
    maxContext: "262K",
    maxOutput: "262K",
    latency: 16,
    icon: "nights_stay",
    featured: true,
    routing: {
      modelId: "kimi-k2.5",
      description:
        "1T-parameter open-source multimodal thinking agent — strong visual coding, BrowseComp, and long-context performance",
      strongestFor: [
        "open_weight",
        "coding",
        "reasoning",
        "image_understanding",
        "document_understanding",
        "long_context",
        "tool_use",
        "low_cost",
      ],
      scores: {
        code: 0.88,
        math: 0.87,
        writing: 0.86,
        analysis: 0.87,
        qa_factual: 0.86,
        translation: 0.83,
        extraction: 0.87,
        classification: 0.85,
        creative: 0.85,
        reasoning: 0.89,
        long_context: 0.94,
        tool_use: 0.93,
        multimodal: 0.96,
        safety: 0.89,
      },
    },
    supportedParams: [
      "model",
      "messages",
      "max_tokens",
      "max_completion_tokens",
      "temperature",
      "top_p",
      "thinking",
      "presence_penalty",
      "frequency_penalty",
      "n",
      "tool_choice",
      "stream",
      "stop",
    ],
  },
  "kimi-k2.7-code": {
    family: "moonshot",
    inputPricePerMillion: 0.95,
    outputPricePerMillion: 4,
    cacheReadPricePerMillion: 0.19,
    cacheCreationPricePerMillion: 0.95,
    pricingSourceModelId: "moonshotai/kimi-k2.7-code",
    pricingSourceUrl: "https://openrouter.ai/moonshotai/kimi-k2.7-code",
    pricingSourceCheckedAt: "2026-06-16",
    maxContextTokens: 262_144,
    description:
      "1T total, 32B active (MoE) specialized for long-horizon software engineering. Multi-turn tool calling with automatic context caching.",
    strongestFor:
      "Long-horizon software engineering, agentic coding workflows, multi-turn tool calling",
    releasedAt: "2026-06-12",
    displayName: "Kimi K2.7 Code",
    version: "K2.7 Code",
    maxContext: "256K",
    maxOutput: "256K",
    latency: 16,
    icon: "nights_stay",
    featured: true,
    supportedParams: [
      "model",
      "messages",
      "max_tokens",
      "max_completion_tokens",
      "temperature",
      "top_p",
      "thinking",
      "presence_penalty",
      "frequency_penalty",
      "n",
      "tool_choice",
      "stream",
      "stop",
    ],
    routing: {
      modelId: "kimi-k2.7-code",
      description:
        "Specialized coding MoE (1T total, 32B active) — long-horizon software engineering with automatic caching",
      strongestFor: [
        "coding",
        "reasoning",
        "tool_use",
        "long_context",
        "image_understanding",
      ],
      scores: {
        code: 0.93,
        math: 0.89,
        writing: 0.85,
        analysis: 0.88,
        qa_factual: 0.86,
        translation: 0.82,
        extraction: 0.87,
        classification: 0.84,
        creative: 0.84,
        reasoning: 0.91,
        long_context: 0.95,
        tool_use: 0.95,
        multimodal: 0.92,
        safety: 0.88,
      },
    },
  },

  // ========================================
  // STEPFUN MODELS
  // Verified against OpenRouter model pricing (checked 2026-06-16).
  // ========================================
  "step-3.7-flash": {
    family: "stepfun",
    inputPricePerMillion: 0.2,
    outputPricePerMillion: 1.15,
    cacheReadPricePerMillion: 0.04,
    cacheCreationPricePerMillion: 0.2,
    pricingSourceModelId: "stepfun/step-3.7-flash",
    pricingSourceUrl: "https://openrouter.ai/stepfun/step-3.7-flash",
    pricingSourceCheckedAt: "2026-06-16",
    maxContextTokens: 262_144,
    description:
      "Most cost-effective multimodal model. 198B total, ~11B active (sparse MoE). Supports vision + video input with tunable reasoning tiers.",
    strongestFor:
      "Multimodal analysis, agentic workflows, coding tasks, cost optimization",
    releasedAt: "2026-02",
    displayName: "Step 3.7 Flash",
    version: "3.7 Flash",
    maxContext: "256K",
    maxOutput: "256K",
    latency: 10,
    supportedParams: [
      "model",
      "messages",
      "temperature",
      "top_p",
      "max_tokens",
      "stream",
      "stop",
      "tools",
      "tool_choice",
    ],
    routing: {
      modelId: "step-3.7-flash",
      description:
        "Most cost-effective multimodal — sparse MoE with vision/video input, tunable reasoning",
      strongestFor: [
        "low_cost",
        "low_latency",
        "text_generalist",
        "coding",
        "image_understanding",
        "tool_use",
      ],
      scores: {
        code: 0.85,
        math: 0.83,
        writing: 0.83,
        analysis: 0.84,
        qa_factual: 0.84,
        translation: 0.8,
        extraction: 0.84,
        classification: 0.83,
        creative: 0.82,
        reasoning: 0.85,
        long_context: 0.88,
        tool_use: 0.87,
        multimodal: 0.91,
        safety: 0.87,
      },
    },
  },

  // ========================================
  // XIAOMI MIMO MODELS
  // Verified against OpenRouter model pricing (checked 2026-06-16).
  // ========================================
  "mimo-v2.5": {
    family: "xiaomi",
    inputPricePerMillion: 0.14,
    outputPricePerMillion: 0.28,
    cacheReadPricePerMillion: 0.0028,
    cacheCreationPricePerMillion: 0.14,
    pricingSourceModelId: "xiaomi/mimo-v2.5",
    pricingSourceUrl: "https://openrouter.ai/xiaomi/mimo-v2.5",
    pricingSourceCheckedAt: "2026-08-26",
    pricingNotes:
      "Corrected from a stale $1/$2/$0.02 (~7x too high) to the modal rate set by Xiaomi + Parasail across 5 endpoints; cache-read uses Xiaomi's rate, Parasail's being an outlier for that field.",
    maxContextTokens: 1_000_000,
    description:
      "1T total, 42B active (MoE). Pro-level agentic performance at roughly a third of MiMo V2.5-Pro's cost.",
    strongestFor:
      "Agentic reasoning, step-by-step thinking, tool use, balanced cost-performance",
    releasedAt: "2026-02",
    displayName: "MiMo V2.5",
    version: "V2.5",
    maxContext: "1M",
    maxOutput: "16K",
    latency: 16,
    supportedParams: [
      "model",
      "messages",
      "temperature",
      "top_p",
      "max_tokens",
      "stream",
      "stop",
      "tools",
      "tool_choice",
      "thinking",
    ],
    routing: {
      modelId: "mimo-v2.5",
      description:
        "Balanced MoE (1T total, 42B active) — pro-level agentic performance at mid-tier cost",
      strongestFor: [
        "coding",
        "reasoning",
        "tool_use",
        "long_context",
        "text_generalist",
      ],
      scores: {
        code: 0.88,
        math: 0.87,
        writing: 0.85,
        analysis: 0.87,
        qa_factual: 0.86,
        translation: 0.83,
        extraction: 0.87,
        classification: 0.85,
        creative: 0.84,
        reasoning: 0.89,
        long_context: 0.93,
        tool_use: 0.91,
        multimodal: 0.87,
        safety: 0.88,
      },
    },
  },
  "mimo-v2.5-pro": {
    family: "xiaomi",
    inputPricePerMillion: 0.435,
    outputPricePerMillion: 0.87,
    cacheReadPricePerMillion: 0.0036,
    cacheCreationPricePerMillion: 0.435,
    pricingSourceModelId: "xiaomi/mimo-v2.5-pro",
    pricingSourceUrl: "https://openrouter.ai/xiaomi/mimo-v2.5-pro",
    pricingSourceCheckedAt: "2026-08-26",
    pricingNotes:
      "Input/output re-verified against 7 OpenRouter endpoints — still the modal rate (Xiaomi + AtlasCloud), not stale despite superficially matching deepseek-v4-pro's old corrected figure (coincidence, confirmed independently). Cache-read corrected from $0.025 to the modal $0.0036.",
    maxContextTokens: 1_050_000,
    description:
      "Best base pricing among frontier models. 1.02T total, 42B active (MoE with hybrid-attention). Surpasses GPT-5.5 on SWE-Bench Pro.",
    strongestFor:
      "Complex software engineering, autonomous agents, large codebase reasoning, frontier-level coding",
    releasedAt: "2026-04-22",
    displayName: "MiMo V2.5 Pro",
    version: "V2.5 Pro",
    maxContext: "1.05M",
    maxOutput: "131K",
    latency: 18,
    icon: "hub",
    featured: true,
    supportedParams: [
      "model",
      "messages",
      "temperature",
      "top_p",
      "max_tokens",
      "stream",
      "stop",
      "tools",
      "tool_choice",
      "thinking",
    ],
    routing: {
      modelId: "mimo-v2.5-pro",
      description:
        "Frontier coding MoE — surpasses GPT-5.5 on SWE-Bench, best base pricing among frontier models",
      strongestFor: [
        "coding",
        "reasoning",
        "tool_use",
        "long_context",
        "low_cost",
      ],
      scores: {
        code: 0.96,
        math: 0.94,
        writing: 0.89,
        analysis: 0.93,
        qa_factual: 0.91,
        translation: 0.87,
        extraction: 0.92,
        classification: 0.9,
        creative: 0.88,
        reasoning: 0.95,
        long_context: 0.97,
        tool_use: 0.96,
        multimodal: 0.93,
        safety: 0.91,
      },
    },
  },

  // ========================================
  // XAI GROK MODELS
  // Verified against OpenRouter model pricing (checked 2026-05-22).
  // Note: Search is a tool charge, not a model variant.
  // ========================================
  "grok-4.3": {
    family: "xai",
    inputPricePerMillion: 1.25,
    outputPricePerMillion: 2.5,
    cacheReadPricePerMillion: 0.2,
    cacheCreationPricePerMillion: 1.25,
    pricingSourceModelId: "x-ai/grok-4.3",
    pricingSourceUrl: "https://openrouter.ai/x-ai/grok-4.3",
    pricingSourceCheckedAt: "2026-06-16",
    pricingNotes:
      "Tiered pricing for requests >200K total tokens. 159 tokens/sec output speed.",
    maxContextTokens: 1_000_000,
    description:
      "Latest Grok model with 1M context and unlimited output. Fast reasoning with strong agentic capabilities.",
    strongestFor:
      "Long-document analysis, deep research, agentic workflows, cost-effective reasoning",
    releasedAt: "2026-04-30",
    displayName: "Grok 4.3",
    version: "4.3",
    maxContext: "1M",
    maxOutput: "Unlimited",
    latency: 15,
    icon: "memory",
    featured: true,
    supportedParams: [
      "model",
      "messages",
      "temperature",
      "top_p",
      "max_tokens",
      "max_completion_tokens",
      "stream",
      "stop",
      "frequency_penalty",
      "presence_penalty",
      "tools",
      "tool_choice",
      "parallel_tool_calls",
      "response_format",
      "seed",
      "n",
      "logprobs",
      "top_logprobs",
      "logit_bias",
      "user",
      "deferred",
      "search_parameters",
      "reasoning_effort",
    ],
    routing: {
      modelId: "grok-4.3",
      description:
        "Latest Grok with 1M context and unlimited output — fast reasoning with strong tool-calling",
      strongestFor: [
        "text_generalist",
        "reasoning",
        "long_context",
        "tool_use",
        "low_cost",
        "low_latency",
      ],
      scores: {
        code: 0.89,
        math: 0.88,
        writing: 0.86,
        analysis: 0.88,
        qa_factual: 0.87,
        translation: 0.82,
        extraction: 0.88,
        classification: 0.86,
        creative: 0.85,
        reasoning: 0.9,
        long_context: 0.96,
        tool_use: 0.94,
        multimodal: 0.9,
        safety: 0.86,
      },
    },
  },
  "grok-4.6": {
    family: "xai",
    inputPricePerMillion: 2,
    outputPricePerMillion: 6,
    cacheReadPricePerMillion: 0.5,
    cacheCreationPricePerMillion: 2,
    pricingSourceModelId: "x-ai/grok-4.6",
    pricingSourceUrl: "https://openrouter.ai/x-ai/grok-4.6",
    pricingSourceCheckedAt: "2026-08-25",
    pricingNotes:
      "Tiered pricing for requests ≥200K total tokens: the entire request bills at $4/M input, $1/M cached input, $12/M output. No published cache-write price; cacheCreationPricePerMillion is priced as normal input, matching xAI's other Grok entries.",
    maxContextTokens: 500_000,
    description:
      "xAI's frontier model for long-running agents, coding, research, and interactive/visual work. Text and image input, text-only output.",
    strongestFor:
      "Long-running agentic workflows, coding, deep research, visual understanding",
    releasedAt: "2026-08-12",
    displayName: "Grok 4.6",
    version: "4.6",
    maxContext: "500K",
    maxOutput: "Unlimited",
    latency: 14,
    icon: "memory",
    featured: true,
    supportedParams: [
      "model",
      "messages",
      "temperature",
      "top_p",
      "max_tokens",
      "max_completion_tokens",
      "stream",
      "stop",
      "frequency_penalty",
      "presence_penalty",
      "tools",
      "tool_choice",
      "parallel_tool_calls",
      "response_format",
      "seed",
      "n",
      "logprobs",
      "top_logprobs",
      "logit_bias",
      "user",
      "deferred",
      "search_parameters",
      "reasoning_effort",
    ],
    routing: {
      modelId: "grok-4.6",
      description:
        "xAI's frontier Grok — long-running agentic workflows, coding, and visual understanding with 500K context",
      strongestFor: [
        "text_generalist",
        "reasoning",
        "long_context",
        "tool_use",
        "image_understanding",
      ],
      scores: {
        code: 0.91,
        math: 0.9,
        writing: 0.87,
        analysis: 0.9,
        qa_factual: 0.88,
        translation: 0.83,
        extraction: 0.89,
        classification: 0.87,
        creative: 0.86,
        reasoning: 0.92,
        long_context: 0.93,
        tool_use: 0.95,
        multimodal: 0.92,
        safety: 0.87,
      },
    },
  },
} satisfies Record<string, ModelPricing>;

/** Every known model id — the literal key union of OFFICIAL_MODEL_PRICING. */
export type ModelId = keyof typeof OFFICIAL_MODEL_PRICING;

export type ModelCatalogMetadata = {
  displayName?: string;
  version?: string;
  provider?: string;
  maxContext?: string;
  maxOutput?: string;
  latency?: number;
  dataCutoff?: string;
  icon?: string;
  featured?: boolean;
};

// MODEL_CATALOG_METADATA_BY_ID has been merged into OFFICIAL_MODEL_PRICING

const EXTERNAL_MODEL_PREFIXES = ["flash/", "omega/", "pro/"];

export function resolveCanonicalModelId(modelId: string): string | undefined {
  if (MODEL_PRICING[modelId]) return modelId;

  for (const prefix of EXTERNAL_MODEL_PREFIXES) {
    if (modelId.startsWith(prefix)) {
      const canonicalId = modelId.slice(prefix.length);
      if (MODEL_PRICING[canonicalId]) {
        return canonicalId;
      }
    }
  }

  if (modelId.includes("/")) {
    const maybeCanonicalId = modelId.split("/").pop();
    if (maybeCanonicalId && MODEL_PRICING[maybeCanonicalId]) {
      return maybeCanonicalId;
    }
  }

  return undefined;
}

export function normalizeModelId(modelId: string): string {
  return resolveCanonicalModelId(modelId) ?? modelId;
}

const DEFAULT_PRICING: ModelPricing = {
  family: "unknown",
  inputPricePerMillion: 1,
  outputPricePerMillion: 3,
  cacheReadPricePerMillion: 0.1,
  cacheCreationPricePerMillion: 1.25,
  maxContextTokens: 128_000,
  description: "Fallback pricing for unknown models.",
  displayName: "Unknown Model",
  maxContext: "128K",
  maxOutput: "Unspecified",
};

export function getModelPricing(model: string): ModelPricing {
  const canonicalModel = normalizeModelId(model);

  // Exact match
  const pricing = MODEL_PRICING[canonicalModel];
  if (pricing) return pricing;

  // Prefix match — sort keys longest-first so more specific keys match before shorter ones
  // (e.g., "gpt-4o-mini" matches before "gpt-4o")
  const sortedKeys = Object.keys(MODEL_PRICING).sort(
    (a, b) => b.length - a.length,
  );
  for (const key of sortedKeys) {
    if (canonicalModel.startsWith(key)) {
      return MODEL_PRICING[key];
    }
  }

  // Nothing in MODEL_PRICING matched, exactly or by prefix. Every branch below
  // returns DEFAULT_PRICING ($1/M in, $3/M out) — a ~5x under-charge for a
  // frontier model. Logged so a service_models row added without a matching
  // MODEL_PRICING entry is observable instead of silently under-billing.
  console.warn(
    "[PRICING] No MODEL_PRICING entry; billing at DEFAULT_PRICING fallback",
    {
      model,
      canonicalModel,
      inputPricePerMillion: DEFAULT_PRICING.inputPricePerMillion,
      outputPricePerMillion: DEFAULT_PRICING.outputPricePerMillion,
    },
  );

  // Infer family from model name
  if (
    canonicalModel.includes("claude") ||
    canonicalModel.includes("anthropic")
  ) {
    return { ...DEFAULT_PRICING, family: "anthropic" };
  }
  if (
    canonicalModel.includes("gpt") ||
    canonicalModel.startsWith("o1") ||
    canonicalModel.startsWith("o3") ||
    canonicalModel.startsWith("o4")
  ) {
    return { ...DEFAULT_PRICING, family: "openai" };
  }
  if (canonicalModel.includes("gemini")) {
    return { ...DEFAULT_PRICING, family: "google" };
  }
  if (canonicalModel.includes("grok")) {
    return { ...DEFAULT_PRICING, family: "xai" };
  }
  if (canonicalModel.includes("deepseek")) {
    return { ...DEFAULT_PRICING, family: "deepseek" };
  }
  if (canonicalModel.includes("minimax")) {
    return { ...DEFAULT_PRICING, family: "minimax" };
  }
  if (canonicalModel.includes("glm")) {
    return { ...DEFAULT_PRICING, family: "zai" };
  }
  if (canonicalModel.includes("qwen")) {
    return { ...DEFAULT_PRICING, family: "qwen" };
  }
  if (canonicalModel.includes("kimi")) {
    return { ...DEFAULT_PRICING, family: "moonshot" };
  }
  if (canonicalModel.includes("step")) {
    return { ...DEFAULT_PRICING, family: "stepfun" };
  }
  if (canonicalModel.includes("mimo")) {
    return { ...DEFAULT_PRICING, family: "xiaomi" };
  }

  return DEFAULT_PRICING;
}

export function getModelFamily(model: string): ModelFamily {
  return getModelPricing(model).family;
}

/**
 * Resolves the Standard tier's PREPAID multiplier for a specific model:
 * per-model override (STANDARD_PREPAID_MULTIPLIER_OVERRIDE) > per-family
 * override (STANDARD_PREPAID_MULTIPLIER_FAMILY_OVERRIDE) > the global default
 * (PREPAID_TIER_MULTIPLIERS.standard).
 *
 * Single source of truth for this resolution — both the billing settlement
 * path (proxy-api-job's TierMultiplierResolver) and any UI that displays a
 * Standard-tier price must call this rather than reading
 * PREPAID_TIER_MULTIPLIERS.standard directly, or they'll show/charge the
 * flat default even for a model with its own override.
 */
export function getStandardTierMultiplier(modelId: string): number {
  const canonicalModelId = normalizeModelId(modelId) as ModelId;

  const modelOverride = STANDARD_PREPAID_MULTIPLIER_OVERRIDE[canonicalModelId];
  if (modelOverride !== undefined) return modelOverride;

  const family = getModelPricing(canonicalModelId).family;
  const familyOverride = STANDARD_PREPAID_MULTIPLIER_FAMILY_OVERRIDE[family];
  if (familyOverride !== undefined) return familyOverride;

  return PREPAID_TIER_MULTIPLIERS.standard;
}

/**
 * Resolves the Economy tier's PREPAID multiplier for a specific model.
 * Mirrors getStandardTierMultiplier's resolution order:  per-model override
 * (ECONOMY_PREPAID_MULTIPLIER_OVERRIDE) > per-family override
 * (ECONOMY_PREPAID_MULTIPLIER_FAMILY_OVERRIDE) > the global default
 * (PREPAID_TIER_MULTIPLIERS.economy).
 */
export function getEconomyTierMultiplier(modelId: string): number {
  const canonicalModelId = normalizeModelId(modelId) as ModelId;

  const modelOverride = ECONOMY_PREPAID_MULTIPLIER_OVERRIDE[canonicalModelId];
  if (modelOverride !== undefined) return modelOverride;

  const family = getModelPricing(canonicalModelId).family;
  const familyOverride = ECONOMY_PREPAID_MULTIPLIER_FAMILY_OVERRIDE[family];
  if (familyOverride !== undefined) return familyOverride;

  return PREPAID_TIER_MULTIPLIERS.economy;
}

/**
 * Resolves the PREPAID-wallet multiplier for any tier + model. Dispatches to
 * getEconomyTierMultiplier/getStandardTierMultiplier for the two overridable
 * tiers; Premium is always PREPAID_TIER_MULTIPLIERS.premium (1.0x, never
 * overridden).
 */
export function getPrepaidTierMultiplier(tier: ProviderTier, modelId: string): number {
  if (tier === "economy") return getEconomyTierMultiplier(modelId);
  if (tier === "standard") return getStandardTierMultiplier(modelId);
  return PREPAID_TIER_MULTIPLIERS.premium;
}

/**
 * Resolves the BONUS-wallet multiplier for a tier and model. Bonus uses the
 * exact same override cascade as prepaid. Premium is never eligible for bonus
 * credit, so it isn't a valid input here.
 */
export function getBonusTierMultiplier(
  tier: "economy" | "standard",
  modelId: string,
): number {
  return getPrepaidTierMultiplier(tier, modelId);
}

// ========================================
// MODEL CAPABILITIES (for agent-worker)
// ========================================

/** Strongest capability labels for routing */
export type ModelCapabilityLabel =
  | "text_generalist"
  | "coding"
  | "reasoning"
  | "math"
  | "image_understanding"
  | "document_understanding"
  | "long_context"
  | "tool_use"
  | "high_safety"
  | "open_weight"
  | "low_cost"
  | "low_latency";

/** Per-model capability scores [0-1] */
export interface ModelCapabilityScores {
  /** Code generation and software engineering */
  code: number;
  /** Mathematical reasoning */
  math: number;
  /** General writing ability */
  writing: number;
  /** Analysis and synthesis */
  analysis: number;
  /** Question answering and factual recall */
  qa_factual: number;
  /** Translation quality */
  translation: number;
  /** Information extraction from text */
  extraction: number;
  /** Classification tasks */
  classification: number;
  /** Creative writing */
  creative: number;
  /** Abstract reasoning */
  reasoning: number;
  /** Long-context retention (128K+ context) */
  long_context: number;
  /** Tool use and agentic workflows */
  tool_use: number;
  /** Multimodal understanding (vision, etc) */
  multimodal: number;
  /** Safety and alignment */
  safety: number;
}

/** Complete model capability configuration */
export interface ModelCapabilities {
  modelId: string;
  description: string;
  strongestFor: ModelCapabilityLabel[];
  scores: ModelCapabilityScores;
}

/**
 * Model capability scores [0-1] for agent-worker model selection
 * Higher = better at that capability
 * Source: LMSYS Arena, SWE-Bench, provider benchmarks
 */
// MODEL_CAPABILITIES_BY_ID has been merged into OFFICIAL_MODEL_PRICING

export const MODEL_PRICING: Record<string, ModelPricing> =
  OFFICIAL_MODEL_PRICING;

export const MODEL_CATALOG_METADATA: Record<string, ModelCatalogMetadata> =
  Object.fromEntries(
    Object.entries(MODEL_PRICING).flatMap(([modelId, pricing]) => {
      const {
        displayName,
        version,
        latency,
        maxContext,
        dataCutoff,
        icon,
        featured,
      } = pricing;

      if (!displayName) return [];
      return [
        [
          modelId,
          {
            displayName,
            version,
            maxContext,
            latency,
            dataCutoff,
            icon,
            featured,
          },
        ],
      ];
    }),
  ) as Record<string, ModelCatalogMetadata>;

/**
 * Check if a model belongs to the Anthropic family
 * Used for determining which client adapter to use
 */
export function isAnthropicFamily(model: string): boolean {
  const family = getModelFamily(model);
  return family === "anthropic";
}

// ========================================
// SHARED CONSUMPTION CALCULATION
// ========================================
//
// NOTE: The legacy `calculateConsumption`/`ConsumptionResult` waterfall
// (included balance -> prepaid balance) has been removed. It had no remaining
// call sites.
//
// Cost is now rated ONCE against the tier (PREPAID_TIER_MULTIPLIERS[tier]),
// then that single amount is split across wallets bonus-first by
// SpendingTransactionRepository.deductBalance. There is no per-wallet rate:
// which wallet pays does not change what the request costs.
